import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, CheckCircle, Mail, CreditCard, RefreshCw, Plus, Package, ChevronDown, ChevronUp, Upload, X, Image, DollarSign, User, Phone, MapPin, Percent, FileText, Check, Clock, XCircle, Eye, Gift, RotateCcw, AlertTriangle, Lock, Fingerprint, EyeOff, HelpCircle, MessageSquare, AlertCircle, Key, Info, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import useBiometricAuth from "@/hooks/useBiometricAuth";
import { useHaptics } from "@/hooks/useHaptics";
import MessagingSection from "@/components/MessagingSection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import OnboardingModal from "@/components/OnboardingModal";
import { ImageLightbox } from "@/components/ImageLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Consignor session auth header (magic link / password login token)
const consignorAuth = (extra = {}) => {
  const t = localStorage.getItem('consignorToken');
  return t ? { ...extra, headers: { ...(extra.headers || {}), Authorization: `Bearer ${t}` } } : extra;
};
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f87e31a4-f19a-4a3f-9c26-c5ad57e131e1/artifacts/vh1p37dl_IMG_0092.png";

// Helper function to extract error message from API responses
// Handles both string errors and Pydantic validation error arrays
const getErrorMessage = (error, fallback = "An error occurred") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // Pydantic validation error format: [{type, loc, msg, input, url}]
    const firstError = detail[0];
    if (firstError?.msg) return firstError.msg;
    if (firstError?.message) return firstError.message;
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const PAYMENT_METHODS = [
  { id: "check", label: "Check", needsDetails: false },
  { id: "venmo", label: "Venmo", needsDetails: true, placeholder: "@username" },
  { id: "paypal", label: "PayPal", needsDetails: true, placeholder: "email or @username" },
  { id: "zelle", label: "Zelle", needsDetails: true, placeholder: "email or phone number" },
  { id: "cashapp", label: "CashApp", needsDetails: true, placeholder: "$cashtag" },
  { id: "applepay", label: "Apple Pay", needsDetails: true, placeholder: "phone number" }
];

// Consignor Messaging Section - Light themed to match other sections
function ConsignorMessagingSection({ email, userName }) {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/conversations/consignor/my-conversation`,
        consignorAuth()
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && email) {
      fetchMessages();
    }
  }, [expanded, email]);

  useEffect(() => {
    if (expanded) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, expanded]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      await axios.post(
        `${API}/conversations/consignor/send`,
        { content: newMessage.trim(), sender_name: userName },
        consignorAuth()
      );
      setNewMessage("");
      await fetchMessages();
      toast.success("Message sent!");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  const formatDateSeparator = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (msgDate.getTime() === today.getTime()) {
      return "Today";
    } else if (msgDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
    }
  };

  const getDateKey = (isoString) => {
    const date = new Date(isoString);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  const unreadCount = messages.filter(m => m.sender_type === "admin" && !m.read).length;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 transition-colors ${
          expanded ? 'bg-purple-500/10' : 'bg-gray-50 hover:bg-gray-100'
        }`}
        data-testid="toggle-messages-section"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            expanded ? 'bg-purple-500' : 'bg-gray-200'
          }`}>
            <MessageSquare className={`w-4 h-4 ${expanded ? 'text-white' : 'text-gray-600'}`} />
          </div>
          <span className={`font-medium ${expanded ? 'text-purple-700' : 'text-[#1A1A2E]'}`}>
            Messages {messages.length > 0 ? `(${messages.length})` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-purple-500" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 bg-purple-50/30">
          {/* Messages List */}
          <div className="h-48 overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageSquare className="w-8 h-8 text-gray-300 mb-1" />
                <p className="text-gray-500 text-sm">No messages yet</p>
                <p className="text-gray-400 text-xs">Send a message to contact admin</p>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const prevMsg = messages[index - 1];
                  const showDateSeparator = !prevMsg || getDateKey(msg.sent_at) !== getDateKey(prevMsg.sent_at);
                  
                  return (
                    <div key={msg.id}>
                      {showDateSeparator && (
                        <div className="flex items-center gap-2 my-3">
                          <div className="flex-1 h-px bg-gray-200"></div>
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {formatDateSeparator(msg.sent_at)}
                          </span>
                          <div className="flex-1 h-px bg-gray-200"></div>
                        </div>
                      )}
                      <div className={`flex ${msg.sender_type === "admin" ? "justify-start" : "justify-end"}`}>
                        <div
                          className={`max-w-[80%] rounded-xl px-3 py-2 ${
                            msg.sender_type === "admin"
                              ? "bg-white border border-gray-200 text-gray-800"
                              : "bg-purple-500 text-white"
                          }`}
                        >
                          {msg.sender_type === "admin" && (
                            <p className="text-xs text-gray-500 mb-0.5">Admin</p>
                          )}
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-0.5 ${msg.sender_type === "admin" ? "text-gray-400" : "text-purple-200"}`}>
                            {formatTime(msg.sent_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Send Message */}
          <form onSubmit={handleSend} className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-400"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white rounded-lg px-4 py-2 flex items-center justify-center flex-shrink-0 min-w-[44px]"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default function ConsignmentAgreementForm() {
  const [searchParams] = useSearchParams();
  const isNewConsignor = searchParams.get('new') === 'true';
  
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    items_to_add: "",
    items_description: "",
    custom_split: "",
    additional_info: "",
    payment_method: "",
    payment_details: "",
    rejected_items_preference: "return", // "return" or "donate"
    signature: "",
    signature_date: "",
    agreed_to_terms: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newAgreementPhotos, setNewAgreementPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const fileInputRef = useRef(null);
  
  // State for change payment method flow
  // If ?new=true, skip initial choice and go directly to new agreement form
  const [showInitialChoice, setShowInitialChoice] = useState(!isNewConsignor);
  const [showChangePayment, setShowChangePayment] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingAgreement, setExistingAgreement] = useState(null);
  const [changePaymentEmail, setChangePaymentEmail] = useState("");
  const [changePaymentMethod, setChangePaymentMethod] = useState("");
  const [changePaymentDetails, setChangePaymentDetails] = useState("");
  const [paymentUpdated, setPaymentUpdated] = useState(false);
  
  // State for add more items flow
  const [showAddItems, setShowAddItems] = useState(false);
  const [addItemsEmail, setAddItemsEmail] = useState("");
  const [addItemsAgreement, setAddItemsAgreement] = useState(null);
  const [itemsToAdd, setItemsToAdd] = useState("");
  const [itemsDescription, setItemsDescription] = useState("");
  const [acknowledgedTerms, setAcknowledgedTerms] = useState(false);
  const [itemsAdded, setItemsAdded] = useState(false);
  const [updateEmail, setUpdateEmail] = useState("");
  const [updatePhone, setUpdatePhone] = useState("");
  const [updateAddress, setUpdateAddress] = useState("");
  const [wantsToUpdateContact, setWantsToUpdateContact] = useState(false);
  const [wantsToUpdatePayment, setWantsToUpdatePayment] = useState(false);
  const [updatePaymentMethod, setUpdatePaymentMethod] = useState("");
  const [updatePaymentDetails, setUpdatePaymentDetails] = useState("");
  const [wantsToAddItems, setWantsToAddItems] = useState(false);
  const [updateCustomSplit, setUpdateCustomSplit] = useState("");
  const [itemAdditionSplit, setItemAdditionSplit] = useState(""); // Split for new items being added
  const [useAccountDefaultSplit, setUseAccountDefaultSplit] = useState(true);
  const [updateRejectedItemsPreference, setUpdateRejectedItemsPreference] = useState("return"); // For add items form
  const [updateAdditionalInfo, setUpdateAdditionalInfo] = useState("");
  const [updatePhotos, setUpdatePhotos] = useState([]);
  const [updateSignature, setUpdateSignature] = useState("");
  const [updateSignatureDate, setUpdateSignatureDate] = useState("");
  const [isAddItemsExpanded, setIsAddItemsExpanded] = useState(false);
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(false);
  const [uploadingUpdatePhotos, setUploadingUpdatePhotos] = useState(false);
  const updateFileInputRef = useRef(null);
  
  // Payment history state
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentFilterType, setPaymentFilterType] = useState("all"); // "all", "month", "year", "custom"
  const [paymentFilterMonth, setPaymentFilterMonth] = useState(new Date().getMonth());
  const [paymentFilterYear, setPaymentFilterYear] = useState(new Date().getFullYear());
  const [paymentCustomStartDate, setPaymentCustomStartDate] = useState("");
  const [paymentCustomEndDate, setPaymentCustomEndDate] = useState("");
  const [viewingPaymentImage, setViewingPaymentImage] = useState(null);
  const [loadingPaymentImage, setLoadingPaymentImage] = useState(false);
  
  // View submissions state
  const [showViewSubmissions, setShowViewSubmissions] = useState(false);
  const [viewSubmissionsEmail, setViewSubmissionsEmail] = useState("");
  const [userSubmissions, setUserSubmissions] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [showSubmissionsExpanded, setShowSubmissionsExpanded] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  
  // Password and biometric auth state
  const [loginPassword, setLoginPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [showFirstTimePasswordPrompt, setShowFirstTimePasswordPrompt] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [loginMode, setLoginMode] = useState("email"); // "email", "password", "biometric"
  
  // Password reset state (minimal for stability)
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  
  // What We Look For info section
  const [showWhatWeLookFor, setShowWhatWeLookFor] = useState(false);
  
  // Biometric auth hook
  const { isNative, isAvailable: biometricAvailable, isLoading: biometricLoading, biometryType, biometricLogin, setCredentials, deleteCredentials } = useBiometricAuth();
  
  // Haptic feedback
  const { buttonPress, heavyPress, lightTap, successFeedback, errorFeedback, warningFeedback } = useHaptics();
  
  // Confirmation dialog state
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  
  // Magic link (secure sign-in) state
  const [magicLinkSentTo, setMagicLinkSentTo] = useState("");
  const [verifyingMagicLink, setVerifyingMagicLink] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle magic-link sign-in token from email link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loginToken = params.get('login_token');
    if (!loginToken) return;
    
    (async () => {
      setVerifyingMagicLink(true);
      try {
        const res = await axios.post(`${API}/forms/consignment/verify-login-link`, { token: loginToken });
        const { access_token, user } = res.data;
        localStorage.setItem('consignorToken', access_token);
        localStorage.setItem('consignorEmail', user.email);
        setAddItemsEmail(user.email);
        setHasPassword(user.has_password);
        setShowInitialChoice(false);
        setShowChangePayment(false);
        setShowAddItems(true);
        await loadAgreementData(user.email);
        if (!user.has_password) {
          setShowFirstTimePasswordPrompt(true);
        }
        toast.success("Signed in securely!");
      } catch (error) {
        toast.error(getErrorMessage(error, "Sign-in link is invalid or expired. Please request a new one."));
      } finally {
        setVerifyingMagicLink(false);
        // Remove token from the URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
  }, []);
  
  // Request a secure sign-in link for a consignor email
  const requestMagicLink = async (email) => {
    await axios.post(`${API}/forms/consignment/request-login-link`, { email: email.trim() });
    setMagicLinkSentTo(email.trim());
    setLoginMode("magic-sent");
    toast.success("Check your email! We sent you a secure sign-in link.", { duration: 8000 });
  };

  // Photo upload handler
  const handlePhotoUpload = async (files, isUpdate = false) => {
    if (!files || files.length === 0) return;
    
    const setter = isUpdate ? setUploadingUpdatePhotos : setUploadingPhotos;
    const currentPhotos = isUpdate ? updatePhotos : newAgreementPhotos;
    const photoSetter = isUpdate ? setUpdatePhotos : setNewAgreementPhotos;
    
    setter(true);
    
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      
      const response = await axios.post(`${API}/forms/upload-photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.uploaded_paths) {
        photoSetter([...currentPhotos, ...response.data.uploaded_paths]);
        successFeedback();
        toast.success(`${response.data.uploaded_paths.length} photo(s) uploaded`);
      }
    } catch (error) {
      errorFeedback();
      toast.error("Failed to upload photos");
      console.error("Upload error:", error);
    } finally {
      setter(false);
    }
  };

  const removePhoto = (index, isUpdate = false) => {
    if (isUpdate) {
      setUpdatePhotos(prev => prev.filter((_, i) => i !== index));
    } else {
      setNewAgreementPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Fetch payment history for a client
  const fetchPaymentHistory = async (email) => {
    setLoadingPaymentHistory(true);
    try {
      const response = await axios.get(`${API}/forms/payment-history/${encodeURIComponent(email)}`, consignorAuth());
      setPaymentHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
      setPaymentHistory({ payments: [], total_paid: 0, payment_count: 0 });
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  // Fetch payment image
  const fetchPaymentImage = async (recordId, email) => {
    console.log("Fetching payment image:", { recordId, email });
    
    if (!email) {
      toast.error("Email not available. Please try again.");
      return;
    }
    
    setLoadingPaymentImage(true);
    try {
      const response = await axios.get(
        `${API}/forms/payment-image/${recordId}?email=${encodeURIComponent(email)}`,
        consignorAuth({ responseType: 'blob' })
      );
      console.log("Image response received:", response.data.size, "bytes");
      const imageUrl = URL.createObjectURL(response.data);
      console.log("Image URL created:", imageUrl);
      setViewingPaymentImage(imageUrl);
      console.log("viewingPaymentImage state set");
    } catch (error) {
      console.error("Failed to fetch payment image:", error);
      toast.error("Failed to load payment image");
    } finally {
      setLoadingPaymentImage(false);
    }
  };

  // Filter payments based on selected filter type
  const getFilteredPayments = () => {
    if (!paymentHistory || !paymentHistory.payments) return [];
    
    const payments = paymentHistory.payments;
    
    if (paymentFilterType === "all") {
      return payments;
    }
    
    return payments.filter(payment => {
      const paymentDate = new Date(payment.check_date || payment.created_at);
      
      if (paymentFilterType === "month") {
        return paymentDate.getMonth() === paymentFilterMonth && 
               paymentDate.getFullYear() === paymentFilterYear;
      }
      
      if (paymentFilterType === "year") {
        return paymentDate.getFullYear() === paymentFilterYear;
      }
      
      if (paymentFilterType === "custom") {
        const start = paymentCustomStartDate ? new Date(paymentCustomStartDate) : null;
        const end = paymentCustomEndDate ? new Date(paymentCustomEndDate + "T23:59:59") : null;
        
        if (start && end) {
          return paymentDate >= start && paymentDate <= end;
        } else if (start) {
          return paymentDate >= start;
        } else if (end) {
          return paymentDate <= end;
        }
      }
      
      return true;
    });
  };

  const filteredPayments = getFilteredPayments();
  const filteredTotal = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (checked) => {
    setFormData({ ...formData, agreed_to_terms: checked });
  };

  const handlePaymentMethodChange = (methodId) => {
    setFormData({ ...formData, payment_method: methodId, payment_details: "" });
  };

  const selectedPaymentMethod = PAYMENT_METHODS.find(m => m.id === formData.payment_method);

  // Check for existing agreement (change payment flow) - now sends a secure sign-in link
  const handleCheckExistingAgreement = async () => {
    if (!changePaymentEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setCheckingEmail(true);
    try {
      await axios.post(`${API}/forms/consignment/request-login-link`, { email: changePaymentEmail.trim() });
      toast.success("Check your email! We sent you a secure sign-in link to manage your account.", { duration: 8000 });
      setMagicLinkSentTo(changePaymentEmail.trim());
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send sign-in link"));
    } finally {
      setCheckingEmail(false);
    }
  };

  // Update payment method
  const handleUpdatePaymentMethod = async () => {
    if (!changePaymentMethod) {
      toast.error("Please select a payment method");
      return;
    }
    
    const selectedMethod = PAYMENT_METHODS.find(m => m.id === changePaymentMethod);
    if (selectedMethod?.needsDetails && !changePaymentDetails.trim()) {
      toast.error(`Please enter your ${selectedMethod.label} details`);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/forms/update-payment-method`, {
        email: changePaymentEmail,
        payment_method: changePaymentMethod,
        payment_details: changePaymentDetails
      }, consignorAuth());
      setPaymentUpdated(true);
      toast.success("Payment method updated successfully!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update payment method"));
    } finally {
      setLoading(false);
    }
  };

  // Handle consignor logout - deactivate push token and reset state
  const handleConsignorLogout = async () => {
    lightTap();
    
    // Deactivate push token on logout
    if (addItemsAgreement?.email) {
      try {
        await axios.post(`${API}/live-activity/deactivate-device-token`, {
          user_id: addItemsAgreement.email.toLowerCase(),
          user_type: "consignor"
        });
      } catch (e) {
        console.log("Failed to deactivate push token:", e);
      }
    }
    
    // Reset all state
    localStorage.removeItem('consignorToken');
    localStorage.removeItem('consignorEmail');
    setShowInitialChoice(true);
    setShowAddItems(false);
    setAddItemsAgreement(null);
    setAddItemsEmail("");
    setItemsToAdd("");
    setItemsDescription("");
    setAcknowledgedTerms(false);
    setWantsToUpdateContact(false);
    setWantsToUpdatePayment(false);
    setUpdateEmail("");
    setUpdatePhone("");
    setUpdateAddress("");
    setUpdatePaymentMethod("");
    setUpdatePaymentDetails("");
  };

  // Check for existing agreement (for add items flow)
  const handleCheckAddItemsAgreement = async () => {
    if (!addItemsEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setCheckingEmail(true);
    try {
      // First check if user has a password set
      const passwordCheck = await axios.get(`${API}/forms/consignment/has-password/${encodeURIComponent(addItemsEmail)}`);
      setHasPassword(passwordCheck.data.has_password);
      
      if (!passwordCheck.data.exists) {
        toast.error("No existing agreement found for this email. Please sign a new agreement first.");
        setShowAddItems(false);
        setShowInitialChoice(false);
        setCheckingEmail(false);
        return;
      }
      
      // If has password and biometric available, try biometric first
      if (passwordCheck.data.has_password && biometricAvailable && isNative) {
        const bioResult = await biometricLogin('consignment_portal', {
          reason: 'Login to your consignment account',
          title: 'Consignment Login',
        });
        
        if (bioResult.success && bioResult.credentials) {
          // Use stored credentials to login
          try {
            let loginSuccessful = false;
            
            // Handle passwordless consignor login
            if (bioResult.credentials.password === 'EMAIL_ONLY_LOGIN') {
              // Try existing secure session; otherwise send a fresh sign-in link
              const storedToken = localStorage.getItem('consignorToken');
              if (storedToken) {
                try {
                  await loadAgreementData(bioResult.credentials.username);
                  loginSuccessful = true;
                } catch (sessionErr) {
                  localStorage.removeItem('consignorToken');
                }
              }
              if (!loginSuccessful) {
                await requestMagicLink(bioResult.credentials.username);
                setCheckingEmail(false);
                return;
              }
            } else {
              // Password-based login
              const loginResponse = await axios.post(`${API}/forms/consignment/login`, {
                email: bioResult.credentials.username,
                password: bioResult.credentials.password
              });
              loginSuccessful = loginResponse.data.success;
              if (loginResponse.data.access_token) {
                localStorage.setItem('consignorToken', loginResponse.data.access_token);
                localStorage.setItem('consignorEmail', bioResult.credentials.username.toLowerCase());
              }
              if (loginSuccessful) {
                await loadAgreementData(bioResult.credentials.username);
              }
            }
            
            if (loginSuccessful) {
              // Request and register for push notifications (consignor)
              try {
                const { PushNotifications } = await import('@capacitor/push-notifications');
                const permResult = await PushNotifications.requestPermissions();
                
                if (permResult.receive === 'granted') {
                  await PushNotifications.register();
                  try {
                    const tokenResult = await PushNotifications.getToken();
                    if (tokenResult?.value) {
                      await axios.post(`${API}/live-activity/register-device-token-typed`, {
                        user_id: addItemsEmail.toLowerCase(),
                        device_token: tokenResult.value,
                        user_type: 'consignor'
                      });
                      console.log('Consignor push token registered via biometric login');
                    }
                  } catch (tokenErr) {
                    console.log('Token retrieval skipped:', tokenErr);
                  }
                }
              } catch (pushErr) {
                console.log('Push token registration skipped:', pushErr);
              }
              
              // Biometric login successful
              setCheckingEmail(false);
              return;
            }
          } catch (loginError) {
            // Credentials might be outdated, continue to password login
            console.log('Stored credentials invalid, falling back to password');
          }
        }
        // Biometric failed or cancelled, show password input
        setLoginMode("password");
        setCheckingEmail(false);
        return;
      }
      
      // If has password but no biometric, show password input
      if (passwordCheck.data.has_password) {
        setLoginMode("password");
        setCheckingEmail(false);
        return;
      }
      
      // No password set — use secure emailed sign-in link
      const storedToken = localStorage.getItem('consignorToken');
      const storedEmail = localStorage.getItem('consignorEmail');
      if (storedToken && storedEmail === addItemsEmail.toLowerCase().trim()) {
        try {
          await loadAgreementData(addItemsEmail);
          setCheckingEmail(false);
          return;
        } catch (sessionErr) {
          localStorage.removeItem('consignorToken');
          localStorage.removeItem('consignorEmail');
        }
      }
      await requestMagicLink(addItemsEmail);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to check for existing agreement"));
    } finally {
      setCheckingEmail(false);
    }
  };
  
  // Load agreement data after authentication
  const loadAgreementData = async (email) => {
    try {
      const response = await axios.get(`${API}/forms/check-existing-agreement?email=${encodeURIComponent(email)}`, consignorAuth());
      if (response.data.has_agreement) {
        setAddItemsAgreement(response.data.agreement);
        setUpdateEmail(response.data.agreement.email || email);
        setUpdatePhone(response.data.agreement.phone || "");
        setUpdateAddress(response.data.agreement.address || "");
        setUpdatePaymentMethod(response.data.agreement.payment_method || "");
        setUpdatePaymentDetails(response.data.agreement.payment_details || "");
        setUpdateCustomSplit(response.data.agreement.agreed_percentage || "50/50");
        // Set today's date as default for signature date
        const today = new Date().toISOString().split('T')[0];
        setUpdateSignatureDate(today);
        // Fetch payment history
        fetchPaymentHistory(email);
        // Fetch user submissions
        fetchUserSubmissions(email);
        setLoginMode("email"); // Reset login mode
      } else {
        toast.error("No existing agreement found for this email. Please sign a new agreement first.");
        setShowAddItems(false);
        setShowInitialChoice(false);
      }
    } catch (error) {
      throw error;
    }
  };
  
  // Handle password login
  const handlePasswordLogin = async () => {
    if (!loginPassword.trim()) {
      toast.error("Please enter your password");
      return;
    }
    
    setCheckingEmail(true);
    try {
      const response = await axios.post(`${API}/forms/consignment/login`, {
        email: addItemsEmail,
        password: loginPassword
      });
      
      if (response.data.success) {
        // Store secure session token
        if (response.data.access_token) {
          localStorage.setItem('consignorToken', response.data.access_token);
          localStorage.setItem('consignorEmail', addItemsEmail.toLowerCase().trim());
        }
        // Save credentials for biometric if available
        if (biometricAvailable && isNative) {
          await setCredentials('consignment_portal', addItemsEmail, loginPassword);
        }
        
        // Request and register for push notifications (consignor)
        try {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const permResult = await PushNotifications.requestPermissions();
          
          if (permResult.receive === 'granted') {
            await PushNotifications.register();
            
            // Try to get existing token
            try {
              const tokenResult = await PushNotifications.getToken();
              if (tokenResult?.value) {
                await axios.post(`${API}/live-activity/register-device-token-typed`, {
                  user_id: addItemsEmail.toLowerCase(),
                  device_token: tokenResult.value,
                  user_type: 'consignor'
                });
                console.log('Consignor push token registered');
              }
            } catch (tokenErr) {
              console.log('Token retrieval skipped:', tokenErr);
            }
          }
        } catch (pushErr) {
          console.log('Push notification registration skipped:', pushErr);
        }
        
        // Load agreement data
        await loadAgreementData(addItemsEmail);
        setLoginPassword("");
        toast.success("Login successful!");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Invalid password"));
    } finally {
      setCheckingEmail(false);
    }
  };
  
  // Handle setting a new password
  const handleSetPassword = async () => {
    if (!newPassword.trim()) {
      toast.error("Please enter a password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSettingPassword(true);
    try {
      await axios.post(`${API}/forms/consignment/set-password`, {
        email: addItemsAgreement.email,
        password: newPassword
      }, consignorAuth());
      
      // Save credentials for biometric if available
      if (biometricAvailable && isNative) {
        await setCredentials('consignment_portal', addItemsAgreement.email, newPassword);
        toast.success("Password set! Face ID/Touch ID is now enabled for future logins.");
      } else {
        toast.success("Password set successfully!");
      }
      
      setShowSetPassword(false);
      setNewPassword("");
      setConfirmPassword("");
      setHasPassword(true);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to set password"));
    } finally {
      setSettingPassword(false);
    }
  };

  // Fetch user submissions for display
  const fetchUserSubmissions = async (email) => {
    try {
      const response = await axios.get(`${API}/forms/my-submissions/${encodeURIComponent(email)}`, consignorAuth());
      setUserSubmissions(response.data);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
      setUserSubmissions(null);
    }
  };

  // Submit additional items
  const handleAddItems = async () => {
    // Check what updates are being made
    const hasItems = isAddItemsExpanded && itemsToAdd && parseInt(itemsToAdd) > 0;
    const hasContactUpdate = wantsToUpdateContact && (updateEmail.trim() !== addItemsAgreement.email || updatePhone.trim() || updateAddress.trim());
    const hasPaymentUpdate = wantsToUpdateContact && updatePaymentMethod && updatePaymentMethod !== addItemsAgreement.payment_method;
    const hasProfitSplitUpdate = wantsToUpdateContact && updateCustomSplit && updateCustomSplit !== addItemsAgreement.agreed_percentage;
    const hasAdditionalInfo = updateAdditionalInfo.trim();
    const hasPhotos = updatePhotos.length > 0;
    
    // Validate payment method details if needed
    if (hasPaymentUpdate) {
      const selectedMethod = PAYMENT_METHODS.find(m => m.id === updatePaymentMethod);
      if (selectedMethod?.needsDetails && !updatePaymentDetails.trim()) {
        toast.error(`Please enter your ${selectedMethod.label} details`);
        return;
      }
    }
    
    if (!hasItems && !hasContactUpdate && !hasPaymentUpdate && !hasProfitSplitUpdate && !hasAdditionalInfo && !hasPhotos) {
      toast.error("Please make at least one update or add items");
      return;
    }
    
    // Only require signature if adding items
    if (hasItems) {
      if (!updateSignature.trim()) {
        toast.error("Please provide your signature to add items");
        return;
      }
      if (!updateSignatureDate) {
        toast.error("Please provide the signature date");
        return;
      }
      if (!acknowledgedTerms) {
        toast.error("Please agree to the terms and conditions to add items");
        return;
      }
    }
    
    setLoading(true);
    try {
      // Only include fields that have actually changed
      const emailChanged = wantsToUpdateContact && updateEmail && updateEmail !== addItemsAgreement.email;
      const phoneChanged = wantsToUpdateContact && updatePhone && updatePhone !== addItemsAgreement.phone;
      const addressChanged = wantsToUpdateContact && updateAddress && updateAddress !== addItemsAgreement.address;
      
      await axios.post(`${API}/forms/add-consignment-items`, {
        email: addItemsEmail,
        full_name: addItemsAgreement.full_name,
        items_to_add: hasItems ? parseInt(itemsToAdd) : 0,
        items_description: itemsDescription,
        acknowledged_terms: hasItems ? acknowledgedTerms : true,
        update_email: emailChanged ? updateEmail : null,
        update_phone: phoneChanged ? updatePhone : null,
        update_address: addressChanged ? updateAddress : null,
        update_payment_method: hasPaymentUpdate ? updatePaymentMethod : null,
        update_payment_details: hasPaymentUpdate ? updatePaymentDetails : null,
        update_profit_split: hasProfitSplitUpdate ? updateCustomSplit : null,
        rejected_items_preference: updateRejectedItemsPreference || "return",
        additional_info: updateAdditionalInfo || null,
        photos: updatePhotos,
        signature: hasItems ? updateSignature : null,
        signature_date: hasItems ? updateSignatureDate : null
      });
      setItemsAdded(true);
      
      let successMsg = [];
      if (hasItems) successMsg.push("Items added");
      if (hasContactUpdate) successMsg.push("Contact info updated");
      if (hasPaymentUpdate) successMsg.push("Payment method updated");
      if (hasProfitSplitUpdate) successMsg.push("Profit split updated");
      if (hasAdditionalInfo || hasPhotos) successMsg.push("Additional info saved");
      heavyPress(); // Strong haptic for successful submission
      successFeedback();
      toast.success(successMsg.join(" & ") + " successfully!");
    } catch (error) {
      errorFeedback();
      toast.error(getErrorMessage(error, "Failed to submit"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    heavyPress(); // Haptic on form submit
    
    if (!formData.agreed_to_terms) {
      errorFeedback();
      toast.error("Please agree to the terms and conditions");
      return;
    }
    
    if (!formData.payment_method) {
      errorFeedback();
      toast.error("Please select a payment method");
      return;
    }
    
    const selectedMethod = PAYMENT_METHODS.find(m => m.id === formData.payment_method);
    if (selectedMethod?.needsDetails && !formData.payment_details.trim()) {
      errorFeedback();
      toast.error(`Please enter your ${selectedMethod.label} details`);
      return;
    }

    setLoading(true);

    // Validate items count
    const itemsCount = parseInt(formData.items_to_add) || 0;
    if (itemsCount <= 0) {
      errorFeedback();
      toast.error("Please enter the number of items you're consigning");
      setLoading(false);
      return;
    }

    // Prepare submission data - use custom split if provided, otherwise default to 50/50
    const submissionData = {
      ...formData,
      items_to_add: itemsCount,
      agreed_percentage: formData.custom_split.trim() || "50/50",
      photos: newAgreementPhotos
    };
    delete submissionData.custom_split;

    try {
      await axios.post(`${API}/forms/consignment-agreement`, submissionData);
      setSubmitted(true);
      successFeedback();
      toast.success("Agreement submitted successfully!");
    } catch (error) {
      errorFeedback();
      toast.error(getErrorMessage(error, "Failed to submit agreement"));
    } finally {
      setLoading(false);
    }
  };

  // Payment method updated success screen
  if (paymentUpdated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="payment-updated-success">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-poppins text-2xl font-bold text-[#1A1A2E] mb-2">
              Payment Method Updated!
            </h2>
            <p className="text-[#666] mb-6">
              Your payment method has been successfully updated to <strong>{PAYMENT_METHODS.find(m => m.id === changePaymentMethod)?.label}</strong>.
            </p>
            <Link to="/" onClick={() => lightTap()}>
              <Button className="bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:from-[#7C3AED] hover:to-[#5B21B6] text-white font-semibold px-8 py-3 rounded-lg shadow-lg" data-testid="back-to-home-btn">
                Back to Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Items added success screen
  if (itemsAdded) {
    const hasItems = wantsToAddItems && itemsToAdd && parseInt(itemsToAdd) > 0;
    const hasContactUpdate = wantsToUpdateContact;
    const hasPaymentUpdate = wantsToUpdatePayment;
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="items-added-success">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-poppins text-2xl font-bold text-[#1A1A2E] mb-2">
              Update Successful!
            </h2>
            <div className="text-[#666] mb-6 space-y-1">
              {hasItems && (
                <p>Added <strong>{itemsToAdd} item{parseInt(itemsToAdd) !== 1 ? 's' : ''}</strong> to your consignment.</p>
              )}
              {hasContactUpdate && (
                <p>Contact information has been updated.</p>
              )}
              {hasPaymentUpdate && (
                <p>Payment method updated to <strong>{PAYMENT_METHODS.find(m => m.id === updatePaymentMethod)?.label}</strong>.</p>
              )}
            </div>
            <Link to="/" onClick={() => lightTap()}>
              <Button className="bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-semibold px-8 py-3 rounded-lg shadow-lg" data-testid="back-to-home-btn">
                Back to Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Initial choice screen
  if (showInitialChoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="consignment-choice-page">
        <div className="max-w-2xl mx-auto">
          {/* Back Link and Logo Row */}
          <div className="relative mt-8 mb-6">
            <Link to="/" onClick={() => lightTap()} className="absolute left-0 top-0 inline-flex items-center gap-2 text-white/70 hover:text-[#8B5CF6] transition-colors" data-testid="back-link-top">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-24 h-24 mx-auto rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20"
            >
              <img src={LOGO_URL} alt="Thrifty Curator Logo" className="w-full h-full object-cover" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-poppins text-3xl font-bold text-white mb-2">Consignment</h1>
            <p className="text-white/60">What would you like to do?</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <button
              onClick={() => { buttonPress(); setShowInitialChoice(false); setShowChangePayment(false); }}
              className="w-full bg-white rounded-xl shadow-2xl p-6 hover:shadow-3xl transition-all duration-300 group"
              data-testid="sign-new-agreement-btn"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-poppins text-lg font-bold text-[#1A1A2E]">Sell With Us</h3>
                  <p className="text-[#666] text-sm">New to consigning? Start here to submit your items.</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => { buttonPress(); setShowInitialChoice(false); setShowAddItems(true); }}
              className="w-full bg-white rounded-xl shadow-2xl p-6 hover:shadow-3xl transition-all duration-300 group"
              data-testid="add-more-items-btn"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-poppins text-lg font-bold text-[#1A1A2E]">Manage My Account</h3>
                  <p className="text-[#666] text-sm">Returning consignor? View submissions, payments, or add items.</p>
                </div>
              </div>
            </button>
          </motion.div>

          <Link 
            to="/" 
            onClick={() => lightTap()}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium"
            data-testid="back-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Change payment method flow
  if (showChangePayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="change-payment-page">
        <div className="max-w-2xl mx-auto">
          {/* Back Link and Logo Row */}
          <div className="relative mt-8 mb-6">
            <button 
              onClick={() => { lightTap(); setShowInitialChoice(true); setShowChangePayment(false); setExistingAgreement(null); setChangePaymentEmail(""); }}
              className="absolute left-0 top-0 inline-flex items-center gap-2 text-white/70 hover:text-[#8B5CF6] transition-colors"
              data-testid="back-to-choice-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-24 h-24 mx-auto rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20"
            >
              <img src={LOGO_URL} alt="Thrifty Curator Logo" className="w-full h-full object-cover" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-poppins text-3xl font-bold text-white mb-2">Change Payment Method</h1>
            <p className="text-white/60">Update your payment preferences</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="h-1.5 bg-gradient-to-r from-[#F59E0B] to-[#D97706]" />
            <div className="p-6 space-y-5">
              {!existingAgreement ? (
                // Step 1: Enter email - we email a secure sign-in link
                magicLinkSentTo ? (
                  <div className="text-center py-4" data-testid="change-payment-link-sent">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Mail className="w-6 h-6 text-amber-600" />
                    </div>
                    <p className="font-semibold text-[#1A1A2E] mb-1">Check your email!</p>
                    <p className="text-sm text-[#666] mb-4">
                      We sent a secure sign-in link to <span className="font-medium">{magicLinkSentTo}</span>.
                      Tap it to access your account and update your payment method. It expires in 30 minutes.
                    </p>
                    <button
                      type="button"
                      onClick={() => setMagicLinkSentTo("")}
                      className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                      data-testid="change-payment-link-back"
                    >
                      Use a different email
                    </button>
                  </div>
                ) : (
                <>
                  <div>
                    <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Email Address</Label>
                    <p className="text-xs text-[#888] mb-2">Enter the email you used when signing your agreement. We'll email you a secure sign-in link.</p>
                    <Input
                      type="email"
                      value={changePaymentEmail}
                      onChange={(e) => setChangePaymentEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="border-2 border-gray-200 focus:border-[#F59E0B] rounded-lg"
                      data-testid="change-payment-email"
                    />
                  </div>
                  <Button
                    onClick={handleCheckExistingAgreement}
                    disabled={checkingEmail}
                    className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-white font-semibold py-3 rounded-lg"
                    data-testid="find-agreement-btn"
                  >
                    {checkingEmail ? "Sending..." : "Email Me a Sign-In Link"}
                  </Button>
                </>
                )
              ) : (
                // Step 2: Show current info and allow updating payment method
                <>
                  <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-[#1A1A2E]">
                      <strong>Agreement found for:</strong> {existingAgreement.full_name}
                    </p>
                    {existingAgreement.payment_method && (
                      <p className="text-sm text-[#666] mt-1">
                        Current payment method: <strong>{PAYMENT_METHODS.find(m => m.id === existingAgreement.payment_method)?.label || existingAgreement.payment_method}</strong>
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-[#1A1A2E] mb-3 block">Select New Payment Method *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {PAYMENT_METHODS.map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => { setChangePaymentMethod(method.id); setChangePaymentDetails(""); }}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                            changePaymentMethod === method.id
                              ? "border-[#F59E0B] bg-[#F59E0B]/10 shadow-md"
                              : "border-gray-200 hover:border-[#F59E0B]/50 hover:bg-gray-50"
                          }`}
                          data-testid={`change-payment-${method.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              changePaymentMethod === method.id ? "bg-[#F59E0B]" : "bg-gray-200"
                            }`}>
                              <CreditCard className={`w-5 h-5 ${changePaymentMethod === method.id ? "text-white" : "text-gray-500"}`} />
                            </div>
                            <span className={`font-medium ${changePaymentMethod === method.id ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                              {method.label}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {changePaymentMethod && PAYMENT_METHODS.find(m => m.id === changePaymentMethod)?.needsDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="overflow-hidden"
                    >
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                        {PAYMENT_METHODS.find(m => m.id === changePaymentMethod)?.label} Details *
                      </Label>
                      <Input
                        type="text"
                        value={changePaymentDetails}
                        onChange={(e) => setChangePaymentDetails(e.target.value)}
                        placeholder={PAYMENT_METHODS.find(m => m.id === changePaymentMethod)?.placeholder}
                        className="border-2 border-gray-200 focus:border-[#F59E0B] rounded-lg"
                        data-testid="change-payment-details"
                      />
                    </motion.div>
                  )}

                  <Button
                    onClick={handleUpdatePaymentMethod}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-white font-semibold py-3 rounded-lg"
                    data-testid="update-payment-btn"
                  >
                    {loading ? "Updating..." : "Update Payment Method"}
                  </Button>
                </>
              )}
            </div>
          </motion.div>

          <button 
            onClick={() => { lightTap(); setShowInitialChoice(true); setShowChangePayment(false); setExistingAgreement(null); setChangePaymentEmail(""); }}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium"
            data-testid="back-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>
      </div>
    );
  }

  // Add more items flow
  if (showAddItems) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="add-items-page">
        {/* Forgot Password Modal - rendered at top level */}
        {showForgotPasswordModal && createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px'
            }}
            onClick={() => {
              setShowForgotPasswordModal(false);
              setPasswordResetSent(false);
            }}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '32px',
                width: '100%',
                maxWidth: '400px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                textAlign: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Icon */}
              <div style={{ 
                width: '70px', 
                height: '70px', 
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 20px auto',
                boxShadow: '0 10px 25px rgba(245, 158, 11, 0.3)'
              }}>
                <Key size={32} color="white" />
              </div>
              
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1A1A2E', marginBottom: '12px' }}>
                Reset Your Password
              </h2>
              
              <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.5' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {!passwordResetSent ? (
                <>
                  <div style={{ textAlign: 'left', marginBottom: '16px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '8px' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="your@email.com"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      data-testid="forgot-password-email-input"
                    />
                  </div>

                  <button
                    onClick={async () => {
                      if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
                        toast.error("Please enter a valid email address");
                        return;
                      }
                      setSendingPasswordReset(true);
                      try {
                        await axios.post(`${API}/password-reset/request`, {
                          email: forgotPasswordEmail,
                          user_type: "consignor"
                        });
                        setPasswordResetSent(true);
                      } catch (error) {
                        if (error.response?.status === 429) {
                          toast.error("Too many requests. Please wait a while before trying again.");
                        } else {
                          setPasswordResetSent(true);
                        }
                      } finally {
                        setSendingPasswordReset(false);
                      }
                    }}
                    disabled={sendingPasswordReset || !forgotPasswordEmail}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: sendingPasswordReset ? '#d1d5db' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: sendingPasswordReset ? 'not-allowed' : 'pointer',
                      marginBottom: '16px'
                    }}
                    data-testid="send-reset-link-btn"
                  >
                    {sendingPasswordReset ? "Sending..." : "Send Reset Link"}
                  </button>
                </>
              ) : (
                <div style={{
                  backgroundColor: '#f0fdf4',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px'
                }}>
                  <CheckCircle size={48} color="#10b981" style={{ margin: '0 auto 12px auto', display: 'block' }} />
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
                    Check Your Email!
                  </p>
                  <p style={{ fontSize: '14px', color: '#4b5563' }}>
                    If an account exists with {forgotPasswordEmail}, you'll receive a password reset link shortly.
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setPasswordResetSent(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>,
          document.body
        )}
        
        <div className="max-w-2xl mx-auto">
          {/* Back Link and Logo Row */}
          <div className="relative mt-8 mb-6">
            <button 
              onClick={handleConsignorLogout}
              className="absolute left-0 top-0 inline-flex items-center gap-2 text-white/70 hover:text-[#10B981] transition-colors"
              data-testid="back-to-choice-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              Log Out
            </button>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-24 h-24 mx-auto rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20"
            >
              <img src={LOGO_URL} alt="Thrifty Curator Logo" className="w-full h-full object-cover" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-poppins text-3xl font-bold text-white mb-2">My Consignment Portal</h1>
            <p className="text-white/60">View your submissions, payment history, and manage your account</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="h-1.5 bg-gradient-to-r from-[#10B981] to-[#059669]" />
            <div className="p-6 space-y-5">
              {!addItemsAgreement ? (
                // Step 1: Enter email to find existing agreement
                <>
                  {loginMode === "email" && (
                    <>
                      <div>
                        <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Email Address</Label>
                        <p className="text-xs text-[#888] mb-2">Enter the email you used when signing your agreement</p>
                        <Input
                          type="email"
                          value={addItemsEmail}
                          onChange={(e) => setAddItemsEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="border-2 border-gray-200 focus:border-[#10B981] rounded-lg"
                          data-testid="add-items-email"
                        />
                      </div>
                      <Button
                        onClick={handleCheckAddItemsAgreement}
                        disabled={checkingEmail}
                        className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-semibold py-3 rounded-lg"
                        data-testid="find-agreement-btn"
                      >
                        {checkingEmail ? "Checking..." : "Find My Agreement"}
                      </Button>
                      
                      {/* Face ID / Touch ID button - separate from email continue */}
                      {biometricAvailable && isNative && (
                        <button
                          type="button"
                          disabled={checkingEmail}
                          onClick={async () => {
                            setCheckingEmail(true);
                            console.log('Face ID button clicked');
                            
                            const bioResult = await biometricLogin('consignment_portal', {
                              reason: 'Login to your consignment account',
                              title: 'Consignment Portal',
                            });
                            
                            console.log('Biometric result:', bioResult);
                            
                            if (bioResult.success && bioResult.credentials) {
                              try {
                                const email = bioResult.credentials.username;
                                setAddItemsEmail(email);
                                
                                // Handle passwordless consignor login
                                if (bioResult.credentials.password === 'EMAIL_ONLY_LOGIN') {
                                  await loadAgreementData(email);
                                  toast.success('Welcome back!');
                                } else {
                                  // Password-based login
                                  const loginResponse = await axios.post(`${API}/forms/consignment/login`, {
                                    email: email,
                                    password: bioResult.credentials.password
                                  });
                                  
                                  if (loginResponse.data.success) {
                                    await loadAgreementData(email);
                                    toast.success('Welcome back!');
                                  }
                                }
                              } catch (error) {
                                toast.error("Saved login is outdated. Please login with email.");
                              }
                            } else if (bioResult.needsPassword) {
                              toast.info("No saved login found. Please login with email first.");
                            } else if (bioResult.cancelled) {
                              // User cancelled, do nothing
                            }
                            setCheckingEmail(false);
                          }}
                          className="w-full py-3 text-[#10B981] hover:text-[#059669] flex items-center justify-center gap-2 text-sm font-medium transition-colors border-2 border-[#10B981]/30 hover:border-[#10B981] rounded-lg"
                          data-testid="face-id-login-btn"
                        >
                          <Fingerprint className="w-5 h-5" />
                          Use Face ID / Touch ID
                        </button>
                      )}
                      
                      {/* Forgot Password Button - opens modal */}
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPasswordModal(true);
                          setForgotPasswordEmail("");
                          setPasswordResetSent(false);
                        }}
                        className="w-full text-sm text-amber-600 hover:text-amber-700 flex items-center justify-center gap-2 py-2"
                        data-testid="forgot-password-link-email-screen"
                      >
                        <HelpCircle className="w-4 h-4" />
                        Forgot your password?
                      </button>
                      
                      {/* Help text */}
                      <p className="text-center text-xs text-gray-400 mt-2 px-4">
                        Need more help logging in? Send a message from the homepage.
                      </p>
                    </>
                  )}
                  
                  {loginMode === "magic-sent" && (
                    <div className="text-center py-4" data-testid="magic-link-sent-message">
                      <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Mail className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="font-semibold text-[#1A1A2E] mb-1">Check your email!</p>
                      <p className="text-sm text-[#666] mb-4">
                        We sent a secure sign-in link to <span className="font-medium">{magicLinkSentTo}</span>.
                        Tap the link in that email to access your account. It expires in 30 minutes.
                      </p>
                      <button
                        type="button"
                        onClick={() => setLoginMode("email")}
                        className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                        data-testid="magic-link-back-btn"
                      >
                        Use a different email
                      </button>
                    </div>
                  )}
                  
                  {loginMode === "password" && (
                    <>
                      <div className="text-center mb-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Lock className="w-6 h-6 text-emerald-600" />
                        </div>
                        <p className="text-sm text-[#666]">Enter your password for</p>
                        <p className="font-semibold text-[#1A1A2E]">{addItemsEmail}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Password</Label>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="Enter your password"
                            className="border-2 border-gray-200 focus:border-[#10B981] rounded-lg pr-10"
                            data-testid="login-password"
                            onKeyDown={(e) => e.key === 'Enter' && handlePasswordLogin()}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <Button
                        onClick={handlePasswordLogin}
                        disabled={checkingEmail}
                        className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-semibold py-3 rounded-lg"
                        data-testid="password-login-btn"
                      >
                        {checkingEmail ? "Logging in..." : "Login"}
                      </Button>
                      
                      {/* Forgot Password Button - opens modal */}
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPasswordModal(true);
                          setForgotPasswordEmail(addItemsEmail || "");
                          setPasswordResetSent(false);
                        }}
                        className="w-full text-sm text-amber-600 hover:text-amber-700 flex items-center justify-center gap-2 py-2"
                        data-testid="forgot-password-link"
                      >
                        <HelpCircle className="w-4 h-4" />
                        Forgot your password?
                      </button>
                      
                      {/* Try Face ID again button */}
                      {biometricAvailable && isNative && (
                        <button
                          onClick={handleCheckAddItemsAgreement}
                          className="w-full text-sm text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-2"
                        >
                          <Fingerprint className="w-4 h-4" />
                          Try Face ID Again
                        </button>
                      )}
                      
                      <button
                        onClick={() => { setLoginMode("email"); setLoginPassword(""); setAddItemsEmail(""); }}
                        className="w-full text-sm text-gray-500 hover:text-gray-700"
                      >
                        Use a different email
                      </button>
                      
                      {/* Help text */}
                      <p className="text-center text-xs text-gray-400 mt-4 px-4">
                        Need more help logging in? Send a message from the homepage.
                      </p>
                    </>
                  )}
                </>
              ) : (
                // Step 2: Update form matching original consignment form style
                <div className="space-y-4">
                  {/* Welcome Header */}
                  <div className="text-center pb-2">
                    <p className="text-lg font-semibold text-[#1A1A2E]">Welcome back, {addItemsAgreement.full_name}!</p>
                    <p className="text-sm text-[#666] mt-1">
                      {addItemsAgreement.email} • {addItemsAgreement.agreed_percentage || "50/50"} split
                    </p>
                    {/* Set/Change Password Button */}
                    <button
                      onClick={() => setShowSetPassword(true)}
                      className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1 mx-auto"
                    >
                      <Lock className="w-3 h-3" />
                      {hasPassword ? "Change Password" : "Set Password for Quick Login"}
                    </button>
                  </div>

                  {/* Password Modal - Using Portal for proper rendering */}
                  {showSetPassword && createPortal(
                    <div 
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                      }}
                    >
                      {/* Backdrop */}
                      <div 
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0,0,0,0.5)'
                        }}
                        onClick={() => { setShowSetPassword(false); setNewPassword(""); setConfirmPassword(""); }}
                      />
                      {/* Modal Content */}
                      <div
                        style={{
                          position: 'relative',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          padding: '24px',
                          width: '90%',
                          maxWidth: '380px',
                          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                        }}
                      >
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ 
                              width: '40px', 
                              height: '40px', 
                              backgroundColor: '#d1fae5', 
                              borderRadius: '50%', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center' 
                            }}>
                              <Lock size={20} color="#059669" />
                            </div>
                            <span style={{ fontWeight: '600', color: '#1A1A2E', fontSize: '18px' }}>
                              {hasPassword ? "Change Password" : "Set Password"}
                            </span>
                          </div>
                          <button 
                            type="button"
                            onClick={() => { setShowSetPassword(false); setNewPassword(""); setConfirmPassword(""); }}
                            style={{ background: 'none', border: 'none', padding: '8px', cursor: 'pointer' }}
                          >
                            <X size={24} color="#9ca3af" />
                          </button>
                        </div>

                        {biometricAvailable && isNative && (
                          <div style={{ 
                            backgroundColor: '#eff6ff', 
                            padding: '12px', 
                            borderRadius: '8px', 
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px'
                          }}>
                            <Fingerprint size={20} color="#2563eb" style={{ flexShrink: 0 }} />
                            <span style={{ fontSize: '14px', color: '#1e40af' }}>
                              Setting a password enables Face ID/Touch ID for quick login!
                            </span>
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1A1A2E', marginBottom: '8px' }}>
                              New Password
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type={showPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter password"
                                style={{
                                  width: '100%',
                                  padding: '14px 48px 14px 16px',
                                  border: '2px solid #e5e7eb',
                                  borderRadius: '12px',
                                  fontSize: '16px',
                                  outline: 'none',
                                  boxSizing: 'border-box'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                  position: 'absolute',
                                  right: '12px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '4px'
                                }}
                              >
                                {showPassword ? <EyeOff size={20} color="#9ca3af" /> : <Eye size={20} color="#9ca3af" />}
                              </button>
                            </div>
                          </div>
                          
                          <div>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1A1A2E', marginBottom: '8px' }}>
                              Confirm Password
                            </label>
                            <input
                              type={showPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Confirm password"
                              style={{
                                width: '100%',
                                padding: '14px 16px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '12px',
                                fontSize: '16px',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                            <button
                              type="button"
                              onClick={() => { setShowSetPassword(false); setNewPassword(""); setConfirmPassword(""); }}
                              style={{
                                flex: 1,
                                padding: '14px',
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                color: '#4b5563',
                                fontSize: '15px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSetPassword}
                              disabled={settingPassword || !newPassword || !confirmPassword}
                              style={{
                                flex: 1,
                                padding: '14px',
                                backgroundColor: '#10b981',
                                border: 'none',
                                borderRadius: '12px',
                                color: 'white',
                                fontSize: '15px',
                                fontWeight: '500',
                                cursor: settingPassword || !newPassword || !confirmPassword ? 'not-allowed' : 'pointer',
                                opacity: settingPassword || !newPassword || !confirmPassword ? 0.5 : 1
                              }}
                            >
                              {settingPassword ? "Saving..." : "Save Password"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}

                  {/* First-Time Password Setup Prompt */}
                  {showFirstTimePasswordPrompt && !hasPassword && createPortal(
                    <div 
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                      }}
                    >
                      {/* Backdrop */}
                      <div 
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0,0,0,0.6)'
                        }}
                        onClick={() => setShowFirstTimePasswordPrompt(false)}
                      />
                      {/* Modal Content */}
                      <div
                        style={{
                          position: 'relative',
                          backgroundColor: 'white',
                          borderRadius: '20px',
                          padding: '28px 24px',
                          width: '90%',
                          maxWidth: '360px',
                          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                          textAlign: 'center'
                        }}
                      >
                        {/* Icon */}
                        <div style={{ 
                          width: '70px', 
                          height: '70px', 
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          borderRadius: '50%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          margin: '0 auto 20px auto',
                          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)'
                        }}>
                          <Lock size={32} color="white" />
                        </div>
                        
                        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1A1A2E', marginBottom: '12px' }}>
                          Secure Your Account
                        </h2>
                        
                        <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.5' }}>
                          Set up a password for faster, more secure access to your consignment portal.
                          {biometricAvailable && isNative && (
                            <span style={{ display: 'block', marginTop: '8px', color: '#2563eb', fontWeight: '500' }}>
                              This also enables Face ID / Touch ID!
                            </span>
                          )}
                        </p>

                        {/* Benefits */}
                        <div style={{ 
                          backgroundColor: '#f0fdf4', 
                          borderRadius: '12px', 
                          padding: '16px',
                          marginBottom: '24px',
                          textAlign: 'left'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <CheckCircle size={18} color="#10b981" />
                            <span style={{ fontSize: '14px', color: '#166534' }}>Quick login with password</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <CheckCircle size={18} color="#10b981" />
                            <span style={{ fontSize: '14px', color: '#166534' }}>Protect your account</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <CheckCircle size={18} color="#10b981" />
                            <span style={{ fontSize: '14px', color: '#166534' }}>Access from any device</span>
                          </div>
                        </div>

                        {/* Buttons */}
                        <button
                          type="button"
                          onClick={() => {
                            setShowFirstTimePasswordPrompt(false);
                            setShowSetPassword(true);
                          }}
                          style={{
                            width: '100%',
                            padding: '16px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            marginBottom: '12px',
                            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                          }}
                        >
                          Set Up Password Now
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setShowFirstTimePasswordPrompt(false)}
                          style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '12px',
                            color: '#9ca3af',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          Maybe Later
                        </button>
                        
                        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '16px' }}>
                          You can always set this up later from your account
                        </p>
                      </div>
                    </div>,
                    document.body
                  )}

                  {/* Payment History - Compact */}
                  {paymentHistory && paymentHistory.payments.length > 0 && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-800">Total Earned</span>
                        </div>
                        <span className="text-lg font-bold text-green-600">${paymentHistory.total_paid.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* MY ACCOUNT SECTION */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowAccountDetails(!showAccountDetails)}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${
                        showAccountDetails ? 'bg-blue-500/10' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid="toggle-account-details"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          showAccountDetails ? 'bg-blue-500' : 'bg-gray-200'
                        }`}>
                          <User className={`w-4 h-4 ${showAccountDetails ? 'text-white' : 'text-gray-600'}`} />
                        </div>
                        <span className={`font-medium ${showAccountDetails ? 'text-blue-700' : 'text-[#1A1A2E]'}`}>
                          My Account
                        </span>
                      </div>
                      {showAccountDetails ? <ChevronUp className="w-5 h-5 text-blue-500" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>
                    
                    {showAccountDetails && addItemsAgreement && (
                      <div className="p-4 border-t border-gray-100 bg-blue-50/30 space-y-4">
                        {/* Personal Information */}
                        <div>
                          <h4 className="text-sm font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            Personal Information
                          </h4>
                          <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-50">
                            <div className="flex justify-between items-center p-3">
                              <span className="text-sm text-gray-500">Full Name</span>
                              <span className="text-sm font-medium text-[#1A1A2E]">{addItemsAgreement.full_name}</span>
                            </div>
                            <div className="flex justify-between items-center p-3">
                              <span className="text-sm text-gray-500">Email</span>
                              <span className="text-sm font-medium text-[#1A1A2E]">{addItemsAgreement.email}</span>
                            </div>
                            <div className="flex justify-between items-center p-3">
                              <span className="text-sm text-gray-500">Phone</span>
                              <span className="text-sm font-medium text-[#1A1A2E]">{addItemsAgreement.phone || 'Not provided'}</span>
                            </div>
                            <div className="flex justify-between items-start p-3">
                              <span className="text-sm text-gray-500">Address</span>
                              <span className="text-sm font-medium text-[#1A1A2E] text-right max-w-[60%]">{addItemsAgreement.address || 'Not provided'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Consignment Details */}
                        <div>
                          <h4 className="text-sm font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-emerald-500" />
                            Consignment Details
                          </h4>
                          <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-50">
                            <div className="flex justify-between items-center p-3">
                              <span className="text-sm text-gray-500">Split Percentage</span>
                              <span className="text-sm font-semibold text-emerald-600">{addItemsAgreement.agreed_percentage || '50/50'}</span>
                            </div>
                            <div className="flex justify-between items-center p-3">
                              <span className="text-sm text-gray-500">Payment Method</span>
                              <span className="text-sm font-medium text-[#1A1A2E] capitalize">{addItemsAgreement.payment_method || 'Not set'}</span>
                            </div>
                            {addItemsAgreement.payment_details && (
                              <div className="flex justify-between items-center p-3">
                                <span className="text-sm text-gray-500">Payment Details</span>
                                <span className="text-sm font-medium text-[#1A1A2E]">{addItemsAgreement.payment_details}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center p-3">
                              <span className="text-sm text-gray-500">Agreement Date</span>
                              <span className="text-sm font-medium text-[#1A1A2E]">
                                {addItemsAgreement.submitted_at ? new Date(addItemsAgreement.submitted_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                }) : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Account Summary */}
                        <div>
                          <h4 className="text-sm font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-amber-500" />
                            Account Summary
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center">
                              <div className="text-2xl font-bold text-[#00D4FF]">
                                {userSubmissions?.submissions?.length || 0}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Total Submissions</div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center">
                              <div className="text-2xl font-bold text-emerald-600">
                                ${paymentHistory?.total_paid?.toFixed(2) || '0.00'}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Total Paid</div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center">
                              <div className="text-2xl font-bold text-amber-600">
                                {paymentHistory?.payment_count || 0}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Payments Received</div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center">
                              <div className="text-2xl font-bold text-purple-600">
                                {userSubmissions?.submissions?.filter(s => s.status === 'pending')?.length || 0}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Pending Items</div>
                            </div>
                          </div>
                        </div>

                        {/* Security Status */}
                        <div className="bg-white rounded-lg border border-gray-100 p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">Password Protection</span>
                            </div>
                            {hasPassword ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                Enabled
                              </span>
                            ) : (
                              <button
                                onClick={() => setShowSetPassword(true)}
                                className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium hover:bg-amber-200 transition-colors"
                              >
                                Set Up Now
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Face ID / Touch ID Reset - Only show on native platform */}
                        {isNative && biometricAvailable && (
                          <div className="bg-white rounded-lg border border-blue-100 p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Fingerprint className="w-4 h-4 text-blue-500" />
                                <div>
                                  <span className="text-sm text-gray-600">Face ID / Touch ID</span>
                                  <p className="text-xs text-gray-400">Quick biometric login</p>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  if (!window.confirm("Reset Face ID login? You'll need to log in with your password again to re-enable it.")) return;
                                  try {
                                    const result = await deleteCredentials('consignment_portal');
                                    if (result.success) {
                                      toast.success("Face ID reset successfully", {
                                        description: "Log in with password to set up Face ID again."
                                      });
                                    }
                                  } catch (error) {
                                    toast.error("Failed to reset Face ID");
                                  }
                                }}
                                className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full font-medium hover:bg-blue-200 active:bg-blue-300 transition-colors"
                                style={{ touchAction: 'manipulation' }}
                                data-testid="reset-faceid-consignor-btn"
                              >
                                Reset
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Messaging Section - Styled to match other sections */}
                  {addItemsAgreement && (
                    <ConsignorMessagingSection 
                      email={addItemsAgreement.email}
                      userName={addItemsAgreement.full_name}
                    />
                  )}

                  {/* My Submissions Section */}
                  {userSubmissions && userSubmissions.submissions && userSubmissions.submissions.length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowSubmissionsExpanded(!showSubmissionsExpanded)}
                        className={`w-full flex items-center justify-between p-3 transition-colors ${
                          showSubmissionsExpanded ? 'bg-[#00D4FF]/5' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        data-testid="toggle-submissions-section"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            showSubmissionsExpanded ? 'bg-[#00D4FF]' : 'bg-gray-200'
                          }`}>
                            <FileText className={`w-4 h-4 ${showSubmissionsExpanded ? 'text-white' : 'text-gray-600'}`} />
                          </div>
                          <span className={`font-medium ${showSubmissionsExpanded ? 'text-[#00D4FF]' : 'text-[#1A1A2E]'}`}>
                            My Submissions ({userSubmissions.submissions.length})
                          </span>
                        </div>
                        {showSubmissionsExpanded ? <ChevronUp className="w-5 h-5 text-[#00D4FF]" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </button>
                      
                      {showSubmissionsExpanded && (
                        <div className="p-3 border-t border-gray-100 space-y-2 max-h-64 overflow-y-auto bg-gray-50/50">
                          {userSubmissions.submissions.map((submission, index) => (
                            <div 
                              key={submission.id || index}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { 
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedSubmission(submission); 
                                setShowSubmissionDetails(true); 
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedSubmission(submission); 
                                  setShowSubmissionDetails(true); 
                                }
                              }}
                              className="w-full flex items-center justify-between gap-3 p-2.5 bg-white rounded-lg border border-gray-100 hover:border-[#00D4FF] hover:bg-[#00D4FF]/5 transition-colors cursor-pointer active:bg-[#00D4FF]/10"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {/* Type Icon */}
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  submission.type === 'consignment_agreement' 
                                    ? 'bg-purple-100'
                                    : submission.items_to_add > 0
                                      ? 'bg-emerald-100'
                                      : 'bg-blue-100'
                                }`}>
                                  {submission.type === 'consignment_agreement' ? (
                                    <FileText className="w-3.5 h-3.5 text-purple-600" />
                                  ) : submission.items_to_add > 0 ? (
                                    <Package className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : (
                                    <User className="w-3.5 h-3.5 text-blue-600" />
                                  )}
                                </div>
                                
                                {/* Info */}
                                <div className="min-w-0 flex-1 text-left">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#1A1A2E] truncate">
                                      {submission.type_label}
                                    </span>
                                    <span className="text-xs text-[#888] flex-shrink-0">
                                      {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : ''}
                                    </span>
                                  </div>
                                  {submission.items_to_add > 0 && (
                                    <p className="text-xs text-emerald-600">+{submission.items_to_add} items</p>
                                  )}
                                  {submission.items_accepted !== undefined && submission.items_accepted !== null && submission.approval_status === 'approved' && (
                                    <p className="text-xs text-green-600">{submission.items_accepted} accepted</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* View Icon + Status Badge */}
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {/* Status Badge - Based on submission type */}
                                {submission.type === 'consignment_agreement' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    <CheckCircle className="w-3 h-3" />
                                    Active
                                  </span>
                                ) : submission.items_to_add > 0 ? (
                                  submission.approval_status === 'approved' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      <CheckCircle className="w-3 h-3" />
                                      Approved
                                    </span>
                                  ) : submission.approval_status === 'rejected' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                      <XCircle className="w-3 h-3" />
                                      Rejected
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                      <Clock className="w-3 h-3" />
                                      Pending
                                    </span>
                                  )
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    <CheckCircle className="w-3 h-3" />
                                    Updated
                                  </span>
                                )}
                                <Eye className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* PAYMENT HISTORY SECTION */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${
                        showPaymentHistory ? 'bg-amber-500/10' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid="toggle-payment-history"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          showPaymentHistory ? 'bg-amber-500' : 'bg-gray-200'
                        }`}>
                          <DollarSign className={`w-4 h-4 ${showPaymentHistory ? 'text-white' : 'text-gray-600'}`} />
                        </div>
                        <div className="text-left">
                          <span className={`font-medium ${showPaymentHistory ? 'text-amber-700' : 'text-[#1A1A2E]'}`}>
                            Payment History
                          </span>
                          {paymentHistory && paymentHistory.payments.length > 0 && (
                            <span className="ml-2 text-sm text-green-600 font-semibold">
                              ${paymentHistory.total_paid.toFixed(2)} total
                            </span>
                          )}
                        </div>
                      </div>
                      {showPaymentHistory ? <ChevronUp className="w-5 h-5 text-amber-500" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>
                    
                    {showPaymentHistory && (
                      <div className="p-4 border-t border-gray-100 bg-amber-50/30">
                        {/* Filter Controls */}
                        <div className="mb-4 space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {["all", "month", "year", "custom"].map((type) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setPaymentFilterType(type)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  paymentFilterType === type
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300'
                                }`}
                              >
                                {type === "all" ? "All Time" : type === "month" ? "Month" : type === "year" ? "Year" : "Custom"}
                              </button>
                            ))}
                          </div>
                          
                          {/* Month/Year Selectors */}
                          {paymentFilterType === "month" && (
                            <div className="flex gap-2">
                              <select
                                value={paymentFilterMonth}
                                onChange={(e) => setPaymentFilterMonth(parseInt(e.target.value))}
                                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                              >
                                {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => (
                                  <option key={i} value={i}>{m}</option>
                                ))}
                              </select>
                              <select
                                value={paymentFilterYear}
                                onChange={(e) => setPaymentFilterYear(parseInt(e.target.value))}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                              >
                                {[2024, 2025, 2026, 2027].map((y) => (
                                  <option key={y} value={y}>{y}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {paymentFilterType === "year" && (
                            <select
                              value={paymentFilterYear}
                              onChange={(e) => setPaymentFilterYear(parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                            >
                              {[2024, 2025, 2026, 2027].map((y) => (
                                <option key={y} value={y}>{y}</option>
                              ))}
                            </select>
                          )}
                          
                          {paymentFilterType === "custom" && (
                            <div className="flex gap-2 items-center">
                              <input
                                type="date"
                                value={paymentCustomStartDate}
                                onChange={(e) => setPaymentCustomStartDate(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                              />
                              <span className="text-gray-400">to</span>
                              <input
                                type="date"
                                value={paymentCustomEndDate}
                                onChange={(e) => setPaymentCustomEndDate(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Filtered Total */}
                        {paymentFilterType !== "all" && filteredPayments.length > 0 && (
                          <div className="mb-3 p-3 bg-white rounded-lg border border-amber-200">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">
                                {paymentFilterType === "month" && `${["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][paymentFilterMonth]} ${paymentFilterYear}`}
                                {paymentFilterType === "year" && paymentFilterYear}
                                {paymentFilterType === "custom" && "Selected Period"}
                              </span>
                              <span className="text-lg font-bold text-green-600">${filteredTotal.toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{filteredPayments.length} payment{filteredPayments.length !== 1 ? 's' : ''}</div>
                          </div>
                        )}
                        
                        {/* Payment List */}
                        {loadingPaymentHistory ? (
                          <div className="flex justify-center py-6">
                            <RefreshCw className="w-6 h-6 text-amber-500 animate-spin" />
                          </div>
                        ) : filteredPayments.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <DollarSign className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">No payments found{paymentFilterType !== "all" ? " for this period" : ""}</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {filteredPayments.map((payment, index) => (
                              <div 
                                key={payment.id || index}
                                className="p-3 bg-white rounded-lg border border-gray-100"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                      <DollarSign className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-[#1A1A2E]">
                                        ${(payment.amount || 0).toFixed(2)}
                                        {payment.commission_split && (
                                          <span className="ml-2 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                            {payment.commission_split}
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {payment.check_date ? new Date(payment.check_date).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        }) : 'Date not set'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    {payment.payment_method && (
                                      <span className="text-xs text-gray-500 capitalize">{payment.payment_method}</span>
                                    )}
                                    {payment.check_number && (
                                      <p className="text-xs text-gray-400">#{payment.check_number}</p>
                                    )}
                                  </div>
                                </div>
                                {payment.has_image && (
                                  <button
                                    type="button"
                                    onClick={() => fetchPaymentImage(payment.id, addItemsAgreement?.email || addItemsEmail || existingAgreement?.email)}
                                    className="mt-2 w-full flex items-center justify-center gap-2 text-xs text-[#8B5CF6] bg-[#8B5CF6]/10 hover:bg-[#8B5CF6]/20 py-2 px-3 rounded-lg transition-colors"
                                    data-testid={`view-receipt-${payment.id}`}
                                  >
                                    <Eye className="w-3 h-3" />
                                    View Receipt
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* UPDATE INFORMATION SECTION */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setWantsToUpdateContact(!wantsToUpdateContact)}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${
                        wantsToUpdateContact ? 'bg-[#8B5CF6]/5' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid="toggle-contact-section"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          wantsToUpdateContact ? 'bg-[#8B5CF6]' : 'bg-gray-200'
                        }`}>
                          <User className={`w-4 h-4 ${wantsToUpdateContact ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <span className={`font-medium ${wantsToUpdateContact ? 'text-[#8B5CF6]' : 'text-[#1A1A2E]'}`}>
                          Update My Information
                        </span>
                      </div>
                      {wantsToUpdateContact ? <ChevronUp className="w-5 h-5 text-[#8B5CF6]" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>
                    
                    <AnimatePresence>
                      {wantsToUpdateContact && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 space-y-4 border-t border-gray-100 bg-gray-50/50">
                            <div>
                              <Label className="text-sm font-medium text-[#1A1A2E] mb-1.5 block">Email Address</Label>
                              <Input
                                type="email"
                                value={updateEmail}
                                onChange={(e) => setUpdateEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="border border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                                data-testid="update-email"
                              />
                              <p className="text-xs text-[#888] mt-1 flex items-center gap-1">
                                <span className="text-amber-500">⚠</span> Changing your email will update your login credentials
                              </p>
                              {updateEmail !== addItemsAgreement.email && updateEmail.trim() && (
                                <p className="text-xs text-amber-600 mt-1 font-medium">Will update from: {addItemsAgreement.email}</p>
                              )}
                            </div>

                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Phone Number</Label>
                              <Input
                                type="tel"
                                value={updatePhone}
                                onChange={(e) => setUpdatePhone(e.target.value)}
                                placeholder="(555) 123-4567"
                                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                                data-testid="update-phone"
                              />
                            </div>

                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Address</Label>
                              <Textarea
                                value={updateAddress}
                                onChange={(e) => setUpdateAddress(e.target.value)}
                                placeholder="Street Address, City, State, ZIP Code"
                                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg min-h-[80px]"
                                data-testid="update-address"
                              />
                            </div>

                            {/* Payment Method */}
                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                Payment Method
                                <span className="font-normal text-[#888] ml-2">
                                  (Current: {PAYMENT_METHODS.find(m => m.id === addItemsAgreement.payment_method)?.label || 'Not set'})
                                </span>
                              </Label>
                              <p className="text-xs text-[#888] mb-3">Select a new payment method if you want to change it</p>
                              <div className="grid grid-cols-2 gap-3">
                                {PAYMENT_METHODS.map((method) => (
                                  <button
                                    key={method.id}
                                    type="button"
                                    onClick={() => { setUpdatePaymentMethod(method.id); setUpdatePaymentDetails(""); }}
                                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                                      updatePaymentMethod === method.id
                                        ? "border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-md"
                                        : "border-gray-200 hover:border-[#8B5CF6]/50 hover:bg-gray-50"
                                    }`}
                                    data-testid={`update-payment-${method.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                        updatePaymentMethod === method.id ? "bg-[#8B5CF6]" : "bg-gray-200"
                                      }`}>
                                        <CreditCard className={`w-5 h-5 ${updatePaymentMethod === method.id ? "text-white" : "text-gray-500"}`} />
                                      </div>
                                      <span className={`font-medium ${updatePaymentMethod === method.id ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                                        {method.label}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Payment Details */}
                            {updatePaymentMethod && PAYMENT_METHODS.find(m => m.id === updatePaymentMethod)?.needsDetails && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="overflow-hidden"
                              >
                                <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                  {PAYMENT_METHODS.find(m => m.id === updatePaymentMethod)?.label} Details *
                                </Label>
                                <Input
                                  type="text"
                                  value={updatePaymentDetails}
                                  onChange={(e) => setUpdatePaymentDetails(e.target.value)}
                                  placeholder={PAYMENT_METHODS.find(m => m.id === updatePaymentMethod)?.placeholder}
                                  className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                                  data-testid="update-payment-details"
                                />
                              </motion.div>
                            )}

                            {/* Submit Button for Contact Updates */}
                            <Button
                              onClick={() => setShowUpdateConfirm(true)}
                              disabled={loading}
                              className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:from-[#7C3AED] hover:to-[#5B21B6] text-white font-medium py-2.5 rounded-lg"
                              data-testid="submit-contact-update-btn"
                            >
                              {loading ? "Saving..." : "Save Changes"}
                            </Button>
                            
                            {/* Confirmation Dialog */}
                            <AlertDialog open={showUpdateConfirm} onOpenChange={setShowUpdateConfirm}>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                                    Confirm Information Update
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-left space-y-2">
                                    <p>Are you sure you want to update your information?</p>
                                    {updateEmail !== addItemsAgreement.email && updateEmail.trim() && (
                                      <p className="text-amber-600 font-medium">
                                        Your email will change from {addItemsAgreement.email} to {updateEmail}. This will also update your login credentials.
                                      </p>
                                    )}
                                    <p className="text-sm text-[#666]">These changes will take effect immediately.</p>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={handleAddItems}
                                    className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                                  >
                                    Yes, Update My Information
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* ADD MORE ITEMS SECTION */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setIsAddItemsExpanded(!isAddItemsExpanded)}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${
                        isAddItemsExpanded ? 'bg-[#10B981]/5' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid="toggle-add-items-section"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isAddItemsExpanded ? 'bg-[#10B981]' : 'bg-gray-200'
                        }`}>
                          <Package className={`w-4 h-4 ${isAddItemsExpanded ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <span className={`font-medium ${isAddItemsExpanded ? 'text-[#10B981]' : 'text-[#1A1A2E]'}`}>
                          Add More Items
                        </span>
                      </div>
                      {isAddItemsExpanded ? <ChevronUp className="w-5 h-5 text-[#10B981]" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>
                    
                    <AnimatePresence>
                      {isAddItemsExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 space-y-4 border-t border-gray-100 bg-gray-50/50">
                            <div>
                              <Label className="text-sm font-medium text-[#1A1A2E] mb-1.5 block">Number of Items *</Label>
                              <Input
                                type="number"
                                min="1"
                                value={itemsToAdd}
                                onChange={(e) => setItemsToAdd(e.target.value)}
                                placeholder="Enter number of items"
                                className="border border-gray-200 focus:border-[#10B981] rounded-lg"
                                data-testid="items-to-add"
                              />
                            </div>

                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                Item Description
                                <span className="font-normal text-[#888] ml-2">(Optional)</span>
                              </Label>
                              <Textarea
                                value={itemsDescription}
                                onChange={(e) => setItemsDescription(e.target.value)}
                                placeholder="Brief description of items being added (e.g., '3 vintage dresses, 2 designer handbags')"
                                className="border-2 border-gray-200 focus:border-[#10B981] rounded-lg min-h-[80px]"
                                data-testid="items-description"
                              />
                            </div>

                            {/* Additional Information */}
                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                Additional Information
                                <span className="font-normal text-[#888] ml-2">(Optional)</span>
                              </Label>
                              <Textarea
                                value={updateAdditionalInfo}
                                onChange={(e) => setUpdateAdditionalInfo(e.target.value)}
                                placeholder="Any additional information about your items (brand, condition, size, etc.)"
                                className="border-2 border-gray-200 focus:border-[#10B981] rounded-lg min-h-[80px]"
                                data-testid="update-additional-info"
                              />
                            </div>

                            {/* Photo Upload */}
                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                Upload Photos
                                <span className="font-normal text-[#888] ml-2">(Optional)</span>
                              </Label>
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#10B981] transition-colors">
                                <input
                                  type="file"
                                  ref={updateFileInputRef}
                                  onChange={(e) => handlePhotoUpload(e.target.files, true)}
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  data-testid="update-photo-input"
                                />
                                <button
                                  type="button"
                                  onClick={() => updateFileInputRef.current?.click()}
                                  disabled={uploadingUpdatePhotos}
                                  className="flex flex-col items-center gap-2 w-full"
                                >
                                  {uploadingUpdatePhotos ? (
                                    <RefreshCw className="w-8 h-8 text-[#10B981] animate-spin" />
                                  ) : (
                                    <Upload className="w-8 h-8 text-gray-400" />
                                  )}
                                  <span className="text-sm text-gray-600">
                                    {uploadingUpdatePhotos ? "Uploading..." : "Click to upload photos of your items"}
                                  </span>
                                </button>
                              </div>
                              
                              {updatePhotos.length > 0 && (
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                  {updatePhotos.map((photo, index) => (
                                    <div key={index} className="relative group">
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
                                        alt={`Upload ${index + 1}`}
                                        className="w-full h-20 object-cover rounded-lg"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removePhoto(index, true)}
                                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Profit Split */}
                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                Profit Split
                                <span className="font-normal text-[#888] ml-2">(Current: {addItemsAgreement.agreed_percentage || "50/50"})</span>
                              </Label>
                              <Input
                                type="text"
                                value={updateCustomSplit}
                                onChange={(e) => setUpdateCustomSplit(e.target.value)}
                                placeholder="Leave blank to keep current split"
                                className="border-2 border-gray-200 focus:border-[#10B981] rounded-lg placeholder:text-[#999] placeholder:italic"
                                data-testid="update-custom-split"
                              />
                              <p className="text-xs text-[#888] mt-1">
                                If a different split was agreed upon, enter it here (e.g., "60/40", "70/30")
                              </p>
                            </div>

                            {/* Rejected Items Preference */}
                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                                If any items are not accepted, I prefer them to be:
                              </Label>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  onClick={() => setUpdateRejectedItemsPreference("return")}
                                  className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                                    updateRejectedItemsPreference === "return"
                                      ? "border-[#10B981] bg-[#10B981]/10 shadow-md"
                                      : "border-gray-200 hover:border-[#10B981]/50 hover:bg-gray-50"
                                  }`}
                                  data-testid="add-items-preference-return"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      updateRejectedItemsPreference === "return" ? "bg-[#10B981]" : "bg-gray-200"
                                    }`}>
                                      <RotateCcw className={`w-4 h-4 ${updateRejectedItemsPreference === "return" ? "text-white" : "text-gray-500"}`} />
                                    </div>
                                    <span className={`text-sm font-medium ${updateRejectedItemsPreference === "return" ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                                      Returned to me
                                    </span>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setUpdateRejectedItemsPreference("donate")}
                                  className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                                    updateRejectedItemsPreference === "donate"
                                      ? "border-[#10B981] bg-[#10B981]/10 shadow-md"
                                      : "border-gray-200 hover:border-[#10B981]/50 hover:bg-gray-50"
                                  }`}
                                  data-testid="add-items-preference-donate"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      updateRejectedItemsPreference === "donate" ? "bg-[#10B981]" : "bg-gray-200"
                                    }`}>
                                      <Gift className={`w-4 h-4 ${updateRejectedItemsPreference === "donate" ? "text-white" : "text-gray-500"}`} />
                                    </div>
                                    <span className={`text-sm font-medium ${updateRejectedItemsPreference === "donate" ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                                      Donated
                                    </span>
                                  </div>
                                </button>
                              </div>
                            </div>

                            {/* Terms and Conditions */}
                            <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#6D28D9]/10 rounded-xl p-4 text-sm text-gray-600 border border-[#8B5CF6]/20">
                              <h4 className="font-semibold text-[#1A1A2E] mb-3">Terms & Conditions</h4>
                              
                              {/* Definitions */}
                              <div className="bg-white/60 rounded-lg p-2.5 mb-3 border border-[#8B5CF6]/10 text-xs">
                                <p className="mb-1"><strong className="text-[#8B5CF6]">Consignee:</strong> Thrifty Curator™</p>
                                <p><strong className="text-[#8B5CF6]">Consignor:</strong> You</p>
                              </div>
                              
                              <ul className="space-y-2 max-h-36 overflow-y-auto pr-2">
                                <li>• The profit split will be agreed upon prior to acceptance of any items. Unless otherwise specified, the profit split will be considered 50/50.</li>
                                <li>• There is no guarantee that your item will be sold.</li>
                                <li>• The consignee (Thrifty Curator) has full discretion over how the item is advertised and the price at which it is listed.</li>
                                <li>• The consignee (Thrifty Curator) has the right to refuse any item for sale at any time and will return the item to the consignor (you).</li>
                                <li>• When items are submitted for sale, the consigned item's ownership is relinquished and will be considered the property of the consignee (Thrifty Curator) until sold or released back.</li>
                                <li>• The consignor (you) accepts the condition of the item upon return and waives any claim of damage that occurred in the possession of the consignee (Thrifty Curator).</li>
                              </ul>
                            </div>

                            {/* Signature Section */}
                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Electronic Signature *</Label>
                              <Input
                                type="text"
                                value={updateSignature}
                                onChange={(e) => setUpdateSignature(e.target.value)}
                                placeholder="Type your full name as signature"
                                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg italic"
                                data-testid="update-signature"
                              />
                            </div>

                            <div>
                              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Date *</Label>
                              <Input
                                type="date"
                                value={updateSignatureDate}
                                onChange={(e) => setUpdateSignatureDate(e.target.value)}
                                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                                data-testid="update-signature-date"
                              />
                            </div>

                            <div className="flex items-start gap-3">
                              <Checkbox
                                id="acknowledge-terms"
                                checked={acknowledgedTerms}
                                onCheckedChange={(checked) => setAcknowledgedTerms(checked)}
                                className="w-6 h-6 mt-1 border-2 border-gray-300 data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                                data-testid="acknowledge-terms-checkbox"
                              />
                              <Label htmlFor="acknowledge-terms" className="text-sm text-gray-600 cursor-pointer">
                                I have read and agree to the terms and conditions above. I understand that by typing my name above, I am providing an electronic signature.
                              </Label>
                            </div>

                            {/* Submit Button for Add Items */}
                            <Button
                              onClick={handleAddItems}
                              disabled={loading || !acknowledgedTerms}
                              className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-medium py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="submit-add-items-btn"
                            >
                              {loading ? "Submitting..." : "Add Items & Submit"}
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <button 
            onClick={handleConsignorLogout}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium"
            data-testid="back-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Log Out
          </button>
        </div>

        {/* Submission Details Modal for Add Items View */}
        {lightbox && <ImageLightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />}
        {showSubmissionDetails && selectedSubmission && (
          <div 
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
            onClick={() => setShowSubmissionDetails(false)}
          >
            <div 
              className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 bg-purple-50 border-b flex justify-between items-center">
                <h3 className="font-semibold text-[#1A1A2E]">{selectedSubmission.type_label || 'Submission Details'}</h3>
                <button 
                  onClick={() => setShowSubmissionDetails(false)}
                  className="p-2 hover:bg-white/50 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Status</span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    {selectedSubmission.approval_status || 'Active'}
                  </span>
                </div>

                {/* Date */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Submitted</p>
                  <p className="font-medium text-[#1A1A2E]">
                    {selectedSubmission.submitted_at ? new Date(selectedSubmission.submitted_at).toLocaleString() : 'N/A'}
                  </p>
                </div>

                {/* Name */}
                {selectedSubmission.full_name && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Full Name</p>
                    <p className="font-medium text-[#1A1A2E]">{selectedSubmission.full_name}</p>
                  </div>
                )}

                {/* Email */}
                {selectedSubmission.email && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="font-medium text-[#1A1A2E]">{selectedSubmission.email}</p>
                  </div>
                )}

                {/* Phone */}
                {selectedSubmission.phone && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="font-medium text-[#1A1A2E]">{selectedSubmission.phone}</p>
                  </div>
                )}

                {/* Items Submitted */}
                {(selectedSubmission.items_to_add > 0 || selectedSubmission.items_accepted > 0) && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600 mb-1">Items Submitted</p>
                    <p className="font-bold text-emerald-700 text-xl">
                      {selectedSubmission.items_to_add > 0 ? selectedSubmission.items_to_add : selectedSubmission.items_accepted} items
                    </p>
                  </div>
                )}

                {/* Items Accepted - Show if approved and items_accepted is set */}
                {selectedSubmission.approval_status === 'approved' && selectedSubmission.items_accepted > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 mb-1">Items Accepted for Consignment</p>
                    <p className="font-bold text-green-700 text-xl">{selectedSubmission.items_accepted} items</p>
                  </div>
                )}

                {/* Items Not Accepted + What was done with them */}
                {selectedSubmission.approval_status === 'approved' && 
                 selectedSubmission.items_to_add > 0 && 
                 selectedSubmission.items_accepted >= 0 && 
                 selectedSubmission.items_to_add > selectedSubmission.items_accepted && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600 mb-1">Items Not Accepted</p>
                    <p className="font-bold text-amber-700">{selectedSubmission.items_to_add - selectedSubmission.items_accepted} items</p>
                    {selectedSubmission.rejected_items_action && (
                      <p className="text-sm text-amber-600 mt-2">
                        {selectedSubmission.rejected_items_action === 'donate' 
                          ? '→ Donated to charity' 
                          : '→ Returned to owner'}
                      </p>
                    )}
                  </div>
                )}

                {/* Rejected items action without item count (for legacy data) */}
                {selectedSubmission.approval_status === 'approved' && 
                 selectedSubmission.rejected_items_action && 
                 (!selectedSubmission.items_to_add || selectedSubmission.items_to_add <= selectedSubmission.items_accepted) && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600 mb-1">Items Not Kept</p>
                    <p className="text-sm text-amber-600">
                      {selectedSubmission.rejected_items_action === 'donate' 
                        ? '→ Donated to charity' 
                        : '→ Returned to owner'}
                    </p>
                  </div>
                )}

                {/* Items Description */}
                {selectedSubmission.items_description && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Items Description</p>
                    <p className="font-medium text-[#1A1A2E] whitespace-pre-wrap">{selectedSubmission.items_description}</p>
                  </div>
                )}

                {/* Photos */}
                {selectedSubmission.photos && selectedSubmission.photos.length > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Photos Submitted ({selectedSubmission.photos.length})</p>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedSubmission.photos.map((photo, index) => (
                        <div 
                          key={index} 
                          className="aspect-square rounded-lg overflow-hidden bg-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setLightbox({ images: selectedSubmission.photos, index })}
                          data-testid={`submission-photo-${index}`}
                        >
                          <img 
                            src={photo} 
                            alt={`Item photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Method */}
                {selectedSubmission.payment_method && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                    <p className="font-medium text-[#1A1A2E]">{selectedSubmission.payment_method}</p>
                  </div>
                )}

                {/* Profit Split */}
                {selectedSubmission.agreed_percentage && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Profit Split</p>
                    <p className="font-medium text-[#1A1A2E]">{selectedSubmission.agreed_percentage}</p>
                  </div>
                )}

                {/* Additional Info */}
                {selectedSubmission.additional_info && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Additional Notes</p>
                    <p className="font-medium text-[#1A1A2E] whitespace-pre-wrap">{selectedSubmission.additional_info}</p>
                  </div>
                )}

                {/* Signature */}
                {selectedSubmission.signature && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Signature</p>
                    <p className="font-medium text-[#1A1A2E]">{selectedSubmission.signature}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t bg-gray-50">
                <Button
                  onClick={() => setShowSubmissionDetails(false)}
                  className="w-full bg-[#1A1A2E] hover:bg-[#2A2A3E] text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Image Viewing Modal - For Consignment Portal */}
        {viewingPaymentImage && (
          <div 
            className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              URL.revokeObjectURL(viewingPaymentImage);
              setViewingPaymentImage(null);
            }}
          >
            <div className="relative max-w-3xl max-h-[90vh] w-full">
              <button
                onClick={() => {
                  URL.revokeObjectURL(viewingPaymentImage);
                  setViewingPaymentImage(null);
                }}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <img
                src={viewingPaymentImage}
                alt="Payment Receipt"
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        {/* Loading overlay for payment image - For Consignment Portal */}
        {loadingPaymentImage && (
          <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-3">
              <RefreshCw className="w-8 h-8 text-[#8B5CF6] animate-spin" />
              <p className="text-sm text-gray-600">Loading receipt...</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="consignment-agreement-success">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-poppins text-2xl font-bold text-[#1A1A2E] mb-2">
              Agreement Signed!
            </h2>
            <p className="text-[#666] mb-4">
              Thank you for signing the consignment agreement. We'll send you a confirmation email with next steps.
            </p>
            <div className="mb-6 bg-gradient-to-r from-[#8B5CF6]/20 to-[#6D28D9]/20 border-2 border-[#8B5CF6] rounded-xl p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 bg-[#8B5CF6] rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#1A1A2E] font-semibold text-base">
                  We will contact you via email at the address you provided
                </p>
              </div>
            </div>
            <Link to="/" onClick={() => lightTap()}>
              <Button className="bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:from-[#7C3AED] hover:to-[#5B21B6] text-white font-semibold px-8 py-3 rounded-lg shadow-lg" data-testid="back-to-home-btn">
                Back to Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="consignment-agreement-page">
      <div className="max-w-2xl mx-auto">
        {/* Back Link and Logo Row */}
        <div className="relative mt-8 mb-6">
          {/* Back Link - Aligned with logo */}
          <Link to="/" onClick={() => lightTap()} className="absolute left-0 top-0 inline-flex items-center gap-2 text-white/70 hover:text-[#8B5CF6] transition-colors" data-testid="back-link-top">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          {/* Logo - Centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-24 h-24 mx-auto rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20"
          >
            <img src={LOGO_URL} alt="Thrifty Curator Logo" className="w-full h-full object-cover" />
          </motion.div>
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="font-poppins text-3xl font-bold text-white mb-2">Sell With Us</h1>
          <p className="text-white/60">Submit your items for consignment</p>
        </motion.div>

        {/* What We Look For - Collapsible Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <button
            type="button"
            onClick={() => setShowWhatWeLookFor(!showWhatWeLookFor)}
            className="w-full bg-gradient-to-r from-[#8B5CF6]/10 to-[#6D28D9]/10 border border-[#8B5CF6]/30 rounded-xl p-4 flex items-center justify-between hover:from-[#8B5CF6]/20 hover:to-[#6D28D9]/20 transition-all"
            data-testid="toggle-what-we-look-for"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] rounded-full flex items-center justify-center">
                <Info className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">What We Look For</h3>
                <p className="text-white/60 text-sm">See the types of items and brands we accept</p>
              </div>
            </div>
            {showWhatWeLookFor ? (
              <ChevronUp className="w-5 h-5 text-white/60" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/60" />
            )}
          </button>
          
          <AnimatePresence>
            {showWhatWeLookFor && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="bg-white rounded-b-xl border-x border-b border-[#8B5CF6]/20 p-5 space-y-5">
                  {/* Item Types */}
                  <div>
                    <h4 className="font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4 text-[#8B5CF6]" />
                      Item Types We Accept
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {["Dresses", "Tops", "Jeans", "Outerwear", "Shoes", "Accessories"].map((item) => (
                        <span key={item} className="px-3 py-1.5 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded-full text-sm font-medium">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Clothing Brands */}
                  <div>
                    <h4 className="font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#10B981]" />
                      Clothing Brands We Love
                    </h4>
                    <p className="text-sm text-[#666] leading-relaxed">
                      Spanx, Lululemon, Athleta, Beyond Yoga, Miss Me, Torrid, Tory Burch, Gymshark, Honeylove, Rock Revival, Eileen Fisher, Flax, Free People, Diane Von Furstenberg, Patagonia, The North Face, Harley Davidson, St. John, Everlane, Rag & Bone, Alice + Olivia, Nike, Coach, Michael Kors, Barefoot Dreams, Madewell, Lilly Pulitzer, Kate Spade, Anthropologie, Johnny Was, Farm Rio, Maeve, CVG, No Bull, ZYIA, Figs, Vuori, Alphalete, Buff Bunny, SheFit, Vineyard Vines, Kuhl, Carhartt, Skims, Boden, Levi, Mother Jeans, Agolde, 7 for all mankind, AYR jeans, Frank & Eileen, Veronica Beard, Birddogs, Kerrits, and more!
                    </p>
                  </div>

                  {/* Shoe Brands */}
                  <div>
                    <h4 className="font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#F59E0B]" />
                      Shoe Brands We Love
                    </h4>
                    <p className="text-sm text-[#666] leading-relaxed">
                      Red Wings, Dr Marten, Rothys, Nike, Frye, Ugg, Cole Haan, Merrell, Keen, Chaco, Hey Dudes, Sorel, Hoka, On Running, Dansko, No Bull, Teva, Birkenstock, Ariat, Crocs, Betsy Johnson, Ecco, Brooks, New Balance, Vans, and more!
                    </p>
                  </div>

                  {/* Note */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> This is not an exhaustive list! If you have quality items from brands not listed, we'd still love to hear from you.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="h-1.5 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9]" />
          <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="consignment-agreement-form">
            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Full Legal Name *</Label>
              <Input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="Enter your full legal name"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                data-testid="input-full-name"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Email Address *</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                data-testid="input-email"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Phone Number *</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(555) 123-4567"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                data-testid="input-phone"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Full Address *</Label>
              <Textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="Street Address, City, State, ZIP Code"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg min-h-[80px]"
                data-testid="input-address"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Number of Items to Consign *</Label>
              <Input
                type="number"
                name="items_to_add"
                value={formData.items_to_add}
                onChange={handleChange}
                required
                min="1"
                placeholder="Enter number of items"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                data-testid="input-items-to-add"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Items Description *</Label>
              <Textarea
                name="items_description"
                value={formData.items_description}
                onChange={handleChange}
                required
                placeholder="Describe the items you're consigning (brands, types, conditions, etc.)"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg min-h-[100px]"
                data-testid="input-items-description"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                Custom Profit Split
                <span className="font-normal text-[#888] ml-2">(Default: 50/50)</span>
              </Label>
              <Input
                type="text"
                name="custom_split"
                value={formData.custom_split}
                onChange={handleChange}
                placeholder="Leave blank unless instructed otherwise"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg placeholder:text-[#999] placeholder:italic"
                data-testid="input-custom-split"
              />
              <p className="text-xs text-[#888] mt-1">
                If a different split was agreed upon, enter it here (e.g., "60/40", "70/30")
              </p>
            </div>

            {/* Payment Method Section */}
            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-3 block">Payment Method *</Label>
              <p className="text-xs text-[#888] mb-3">Select how you would like to receive your payment</p>
              <div className="grid grid-cols-2 gap-3">
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => handlePaymentMethodChange(method.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.payment_method === method.id
                        ? "border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-md"
                        : "border-gray-200 hover:border-[#8B5CF6]/50 hover:bg-gray-50"
                    }`}
                    data-testid={`payment-method-${method.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        formData.payment_method === method.id ? "bg-[#8B5CF6]" : "bg-gray-200"
                      }`}>
                        <CreditCard className={`w-5 h-5 ${formData.payment_method === method.id ? "text-white" : "text-gray-500"}`} />
                      </div>
                      <span className={`font-medium ${formData.payment_method === method.id ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                        {method.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Details - shown when a payment method that needs details is selected */}
            {selectedPaymentMethod?.needsDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="overflow-hidden"
              >
                <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                  {selectedPaymentMethod.label} Details *
                </Label>
                <Input
                  type="text"
                  name="payment_details"
                  value={formData.payment_details}
                  onChange={handleChange}
                  placeholder={selectedPaymentMethod.placeholder}
                  className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                  data-testid="input-payment-details"
                />
                <p className="text-xs text-[#888] mt-1">
                  Enter your {selectedPaymentMethod.label} {selectedPaymentMethod.placeholder}
                </p>
              </motion.div>
            )}

            {/* Additional Information Section */}
            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                Additional Information
                <span className="font-normal text-[#888] ml-2">(Optional)</span>
              </Label>
              <Textarea
                name="additional_info"
                value={formData.additional_info}
                onChange={handleChange}
                placeholder="Any additional information about your items (brand, condition, size, etc.)"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg min-h-[80px]"
                data-testid="input-additional-info"
              />
            </div>

            {/* Rejected Items Preference */}
            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                If any items are not accepted for consignment, I prefer them to be:
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, rejected_items_preference: "return" })}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                    formData.rejected_items_preference === "return"
                      ? "border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-md"
                      : "border-gray-200 hover:border-[#8B5CF6]/50 hover:bg-gray-50"
                  }`}
                  data-testid="preference-return"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.rejected_items_preference === "return" ? "bg-[#8B5CF6]" : "bg-gray-200"
                    }`}>
                      <RotateCcw className={`w-5 h-5 ${formData.rejected_items_preference === "return" ? "text-white" : "text-gray-500"}`} />
                    </div>
                    <span className={`font-medium ${formData.rejected_items_preference === "return" ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                      Returned to me
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, rejected_items_preference: "donate" })}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                    formData.rejected_items_preference === "donate"
                      ? "border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-md"
                      : "border-gray-200 hover:border-[#8B5CF6]/50 hover:bg-gray-50"
                  }`}
                  data-testid="preference-donate"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.rejected_items_preference === "donate" ? "bg-[#8B5CF6]" : "bg-gray-200"
                    }`}>
                      <Gift className={`w-5 h-5 ${formData.rejected_items_preference === "donate" ? "text-white" : "text-gray-500"}`} />
                    </div>
                    <span className={`font-medium ${formData.rejected_items_preference === "donate" ? "text-[#1A1A2E]" : "text-gray-600"}`}>
                      Donated
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Photo Upload Section */}
            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">
                Upload Photos
                <span className="font-normal text-[#888] ml-2">(Optional)</span>
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#8B5CF6] transition-colors">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handlePhotoUpload(e.target.files, false)}
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="new-agreement-photo-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhotos}
                  className="flex flex-col items-center gap-2 w-full"
                >
                  {uploadingPhotos ? (
                    <RefreshCw className="w-8 h-8 text-[#8B5CF6] animate-spin" />
                  ) : (
                    <Upload className="w-8 h-8 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-600">
                    {uploadingPhotos ? "Uploading..." : "Click to upload photos of your items"}
                  </span>
                </button>
              </div>
              
              {/* Photo Preview */}
              {newAgreementPhotos.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {newAgreementPhotos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index, false)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Terms and Conditions */}
            <div>
              <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#6D28D9]/10 rounded-xl p-4 mb-4 text-sm text-gray-600 border border-[#8B5CF6]/20">
                <h4 className="font-semibold text-[#1A1A2E] mb-3">Terms & Conditions</h4>
                
                {/* Definitions */}
                <div className="bg-white/60 rounded-lg p-3 mb-4 border border-[#8B5CF6]/10">
                  <h5 className="font-semibold text-[#1A1A2E] mb-2 text-xs uppercase tracking-wide">Definitions</h5>
                  <div className="space-y-2">
                    <p><strong className="text-[#8B5CF6]">Consignee:</strong> Thrifty Curator™ - the business accepting your items to sell on your behalf.</p>
                    <p><strong className="text-[#8B5CF6]">Consignor:</strong> You - the person submitting items to be sold through Thrifty Curator™.</p>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  <li>• The profit split will be agreed upon prior to acceptance of any items. Unless otherwise specified on this form, the profit split will be considered 50/50.</li>
                  <li>• There is no guarantee that your item will be sold.</li>
                  <li>• The consignee (Thrifty Curator) has full discretion over how the item is advertised and the price at which it is listed.</li>
                  <li>• The consignee (Thrifty Curator) has the right to refuse any item for sale at any time and will return the item to the consignor (you).</li>
                  <li>• When items are submitted for sale, the consigned item's ownership is relinquished and will be considered the property of the consignee (Thrifty Curator) for the purposes of sale until sold or released back to the consignor (you).</li>
                  <li>• The consignor (you) accepts the condition of the item upon return and waives any claim of damage that occurred in the possession of the consignee (Thrifty Curator). All items are inspected prior to listing and its condition/defects are listed at the time the item is posted for sale.</li>
                </ul>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Electronic Signature *</Label>
              <Input
                type="text"
                name="signature"
                value={formData.signature}
                onChange={handleChange}
                required
                placeholder="Type your full name as signature"
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg italic"
                data-testid="input-signature"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Date *</Label>
              <Input
                type="date"
                name="signature_date"
                value={formData.signature_date}
                onChange={handleChange}
                required
                className="border-2 border-gray-200 focus:border-[#8B5CF6] rounded-lg"
                data-testid="input-signature-date"
              />
            </div>

            <div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={formData.agreed_to_terms}
                  onCheckedChange={handleCheckboxChange}
                  className="w-6 h-6 mt-1 border-2 border-gray-300 data-[state=checked]:bg-[#8B5CF6] data-[state=checked]:border-[#8B5CF6]"
                  data-testid="checkbox-terms"
                />
                <Label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                  I have read and agree to the terms and conditions above. I understand that by typing my name above, I am providing an electronic signature.
                </Label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.agreed_to_terms}
              className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:from-[#7C3AED] hover:to-[#5B21B6] text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-agreement-btn"
            >
              {loading ? (
                "Submitting..."
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Sign Agreement
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Back to Home - Easy access at bottom */}
        <Link 
          to="/" 
          onClick={() => lightTap()}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium"
          data-testid="back-link"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>
      </div>

      {/* Submission Details Modal - Simple Version */}
      {showSubmissionDetails && selectedSubmission && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={() => setShowSubmissionDetails(false)}
        >
          <div 
            className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 bg-purple-50 border-b flex justify-between items-center">
              <h3 className="font-semibold text-[#1A1A2E]">{selectedSubmission.type_label || 'Submission Details'}</h3>
              <button 
                onClick={() => setShowSubmissionDetails(false)}
                className="p-2 hover:bg-white/50 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">Status</span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  {selectedSubmission.approval_status || 'Active'}
                </span>
              </div>

              {/* Date */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Submitted</p>
                <p className="font-medium text-[#1A1A2E]">
                  {selectedSubmission.submitted_at ? new Date(selectedSubmission.submitted_at).toLocaleString() : 'N/A'}
                </p>
              </div>

              {/* Name */}
              {selectedSubmission.full_name && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Full Name</p>
                  <p className="font-medium text-[#1A1A2E]">{selectedSubmission.full_name}</p>
                </div>
              )}

              {/* Email */}
              {selectedSubmission.email && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-[#1A1A2E]">{selectedSubmission.email}</p>
                </div>
              )}

              {/* Phone */}
              {selectedSubmission.phone && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p className="font-medium text-[#1A1A2E]">{selectedSubmission.phone}</p>
                </div>
              )}

              {/* Items to Add */}
              {selectedSubmission.items_to_add > 0 && (
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600 mb-1">Items to Add</p>
                  <p className="font-bold text-emerald-700 text-xl">{selectedSubmission.items_to_add} items</p>
                </div>
              )}

              {/* Items Description */}
              {selectedSubmission.items_description && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Items Description</p>
                  <p className="font-medium text-[#1A1A2E] whitespace-pre-wrap">{selectedSubmission.items_description}</p>
                </div>
              )}

              {/* Payment Method */}
              {selectedSubmission.payment_method && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                  <p className="font-medium text-[#1A1A2E]">{selectedSubmission.payment_method}</p>
                </div>
              )}

              {/* Profit Split */}
              {selectedSubmission.agreed_percentage && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Profit Split</p>
                  <p className="font-medium text-[#1A1A2E]">{selectedSubmission.agreed_percentage}</p>
                </div>
              )}

              {/* Additional Info */}
              {selectedSubmission.additional_info && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Additional Notes</p>
                  <p className="font-medium text-[#1A1A2E] whitespace-pre-wrap">{selectedSubmission.additional_info}</p>
                </div>
              )}

              {/* Signature */}
              {selectedSubmission.signature && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Signature</p>
                  <p className="font-medium text-[#1A1A2E]">{selectedSubmission.signature}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <Button
                onClick={() => setShowSubmissionDetails(false)}
                className="w-full bg-[#1A1A2E] hover:bg-[#2A2A3E] text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Image Viewing Modal */}
      {viewingPaymentImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => {
            URL.revokeObjectURL(viewingPaymentImage);
            setViewingPaymentImage(null);
          }}
        >
          <div className="relative max-w-3xl max-h-[90vh] w-full">
            <button
              onClick={() => {
                URL.revokeObjectURL(viewingPaymentImage);
                setViewingPaymentImage(null);
              }}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={viewingPaymentImage}
              alt="Payment Receipt"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Loading overlay for payment image */}
      {loadingPaymentImage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-[#8B5CF6] animate-spin" />
            <p className="text-sm text-gray-600">Loading receipt...</p>
          </div>
        </div>
      )}
      
      {/* Onboarding Modal for consignors with existing agreement */}
      {existingAgreement && <OnboardingModal userType="consignor" />}
    </div>
  );
}
